// ============================================
// MEDICATION HANDLING SYSTEM
// ============================================
// 3-layer architecture for class-based clinical reasoning

// ============================================
// TYPES
// ============================================

export interface MedicationClass {
  id: string;
  display_name: string;
  description: string;
  clinical_tags: string[];
  rebuttal_tags: string[];
  step_therapy_level?: number;
  category?: string;
}

export interface Medication {
  name: string;
  generic_name: string;
  brand_names: string[];
  class_id: string;
  synonyms: string[];
  route?:
    | "oral"
    | "injection"
    | "topical"
    | "inhaled"
    | "iv"
    | "patch"
    | "rectal"
    | "nasal"
    | "ophthalmic"
    | "other";
  specialty_tags?: string[];
  controlled?: boolean;
  common?: boolean;
}

export interface MedicationMatch {
  medication: Medication;
  medicationClass: MedicationClass;
  matchType: "exact" | "generic" | "brand" | "synonym" | "fuzzy";
  matchedOn: string;
}

export interface CustomMedication {
  name: string;
  class_id: string | null;
  user_assigned: boolean;
  ai_inferred?: boolean;
}

export interface MedicationSummary {
  class_id: string;
  class_name: string;
  count: number;
  medications: string[];
  rebuttal_phrase: string;
  clinical_tags: string[];
}

// ============================================
// LAYER 2: MEDICATION CLASSES
// ============================================

export const MEDICATION_CLASSES: Record<string, MedicationClass> = {
  // PAIN MANAGEMENT
  nsaid: {
    id: "nsaid",
    display_name: "NSAID",
    description: "Non-steroidal anti-inflammatory drugs",
    clinical_tags: [
      "conservative therapy",
      "first-line",
      "anti-inflammatory",
      "analgesic",
    ],
    rebuttal_tags: ["conservative care attempted", "first-line therapy tried"],
    step_therapy_level: 1,
    category: "pain",
  },
  acetaminophen: {
    id: "acetaminophen",
    display_name: "Acetaminophen",
    description: "Non-opioid analgesic",
    clinical_tags: ["conservative therapy", "first-line", "analgesic"],
    rebuttal_tags: ["conservative care attempted", "first-line therapy tried"],
    step_therapy_level: 1,
    category: "pain",
  },
  opioid: {
    id: "opioid",
    display_name: "Opioid",
    description: "Opioid analgesics",
    clinical_tags: ["controlled substance", "pain management", "last-line"],
    rebuttal_tags: [
      "opioid therapy trialed",
      "controlled substance trial documented",
    ],
    step_therapy_level: 3,
    category: "pain",
  },
  opioid_short_acting: {
    id: "opioid_short_acting",
    display_name: "Short-Acting Opioid",
    description: "Immediate-release opioid analgesics",
    clinical_tags: ["controlled substance", "acute pain", "breakthrough pain"],
    rebuttal_tags: ["short-acting opioid trialed"],
    step_therapy_level: 3,
    category: "pain",
  },
  opioid_long_acting: {
    id: "opioid_long_acting",
    display_name: "Long-Acting Opioid",
    description: "Extended-release opioid analgesics",
    clinical_tags: [
      "controlled substance",
      "chronic pain",
      "around-the-clock pain",
    ],
    rebuttal_tags: ["long-acting opioid therapy established"],
    step_therapy_level: 3,
    category: "pain",
  },
  muscle_relaxant: {
    id: "muscle_relaxant",
    display_name: "Muscle Relaxant",
    description: "Skeletal muscle relaxants",
    clinical_tags: ["conservative therapy", "muscle spasm", "adjunct therapy"],
    rebuttal_tags: ["muscle relaxant therapy attempted"],
    step_therapy_level: 1,
    category: "pain",
  },
  topical_analgesic: {
    id: "topical_analgesic",
    display_name: "Topical Analgesic",
    description: "Topical pain relievers",
    clinical_tags: [
      "conservative therapy",
      "localized treatment",
      "first-line",
    ],
    rebuttal_tags: ["topical therapy attempted"],
    step_therapy_level: 1,
    category: "pain",
  },
  gabapentinoid: {
    id: "gabapentinoid",
    display_name: "Gabapentinoid",
    description: "Gabapentin and pregabalin class",
    clinical_tags: ["neuropathic pain", "anticonvulsant", "nerve pain"],
    rebuttal_tags: ["gabapentinoid therapy trialed"],
    step_therapy_level: 2,
    category: "pain",
  },
  pain_adjunct: {
    id: "pain_adjunct",
    display_name: "Pain Adjunct / Specialty",
    description: "Specialty pain management agents",
    clinical_tags: ["chronic pain", "fibromyalgia", "adjunct therapy"],
    rebuttal_tags: ["adjunct pain therapy trialed"],
    step_therapy_level: 2,
    category: "pain",
  },
  oic_agent: {
    id: "oic_agent",
    display_name: "Opioid-Induced Constipation Agent",
    description: "Peripherally-acting mu-opioid receptor antagonists",
    clinical_tags: ["opioid induced constipation", "pamora", "oic"],
    rebuttal_tags: ["oic therapy indicated with chronic opioid use"],
    step_therapy_level: 2,
    category: "pain",
  },

  // WEIGHT MANAGEMENT
  weight_management: {
    id: "weight_management",
    display_name: "Weight Management Agent",
    description: "Anti-obesity medications",
    clinical_tags: ["obesity", "weight loss", "BMI", "metabolic"],
    rebuttal_tags: ["weight management therapy trialed"],
    step_therapy_level: 2,
    category: "endocrine",
  },

  // HORMONE REPLACEMENT
  hrt_menopause: {
    id: "hrt_menopause",
    display_name: "Menopausal Hormone Therapy",
    description: "Hormone replacement for menopause symptoms",
    clinical_tags: [
      "menopause",
      "vasomotor symptoms",
      "vaginal atrophy",
      "hot flashes",
    ],
    rebuttal_tags: ["hormone replacement therapy indicated"],
    step_therapy_level: 1,
    category: "endocrine",
  },

  // PSYCHIATRIC
  ssri: {
    id: "ssri",
    display_name: "SSRI",
    description: "Selective serotonin reuptake inhibitors",
    clinical_tags: ["first-line antidepressant", "anxiety", "depression"],
    rebuttal_tags: [
      "first-line antidepressant trialed",
      "SSRI trial completed",
    ],
    step_therapy_level: 1,
    category: "psychiatric",
  },
  snri: {
    id: "snri",
    display_name: "SNRI",
    description: "Serotonin-norepinephrine reuptake inhibitors",
    clinical_tags: ["antidepressant", "anxiety", "neuropathic pain"],
    rebuttal_tags: [
      "SNRI therapy trialed",
      "dual-action antidepressant attempted",
    ],
    step_therapy_level: 1,
    category: "psychiatric",
  },
  tca: {
    id: "tca",
    display_name: "Tricyclic Antidepressant",
    description: "Tricyclic antidepressants",
    clinical_tags: [
      "antidepressant",
      "neuropathic pain",
      "migraine prophylaxis",
    ],
    rebuttal_tags: ["TCA therapy attempted"],
    step_therapy_level: 2,
    category: "psychiatric",
  },
  antidepressant_other: {
    id: "antidepressant_other",
    display_name: "Atypical Antidepressant",
    description: "Other antidepressants (bupropion, mirtazapine, trazodone)",
    clinical_tags: ["antidepressant", "atypical mechanism"],
    rebuttal_tags: ["atypical antidepressant trialed"],
    step_therapy_level: 2,
    category: "psychiatric",
  },
  benzodiazepine: {
    id: "benzodiazepine",
    display_name: "Benzodiazepine",
    description: "Benzodiazepine anxiolytics/sedatives",
    clinical_tags: ["controlled substance", "anxiety", "sedative"],
    rebuttal_tags: ["benzodiazepine therapy documented"],
    step_therapy_level: 2,
    category: "psychiatric",
  },
  antipsychotic_typical: {
    id: "antipsychotic_typical",
    display_name: "Typical Antipsychotic",
    description: "First-generation antipsychotics",
    clinical_tags: ["antipsychotic", "first-generation"],
    rebuttal_tags: ["typical antipsychotic trialed"],
    step_therapy_level: 1,
    category: "psychiatric",
  },
  antipsychotic_atypical: {
    id: "antipsychotic_atypical",
    display_name: "Atypical Antipsychotic",
    description: "Second-generation antipsychotics",
    clinical_tags: [
      "antipsychotic",
      "second-generation",
      "mood stabilizer adjunct",
    ],
    rebuttal_tags: ["atypical antipsychotic therapy attempted"],
    step_therapy_level: 2,
    category: "psychiatric",
  },
  mood_stabilizer: {
    id: "mood_stabilizer",
    display_name: "Mood Stabilizer",
    description: "Lithium and anticonvulsant mood stabilizers",
    clinical_tags: ["bipolar disorder", "mood stabilization"],
    rebuttal_tags: ["mood stabilizer therapy trialed"],
    step_therapy_level: 1,
    category: "psychiatric",
  },
  stimulant: {
    id: "stimulant",
    display_name: "Stimulant",
    description: "CNS stimulants for ADHD",
    clinical_tags: ["controlled substance", "ADHD", "narcolepsy"],
    rebuttal_tags: ["stimulant therapy established"],
    step_therapy_level: 1,
    category: "psychiatric",
  },
  non_stimulant_adhd: {
    id: "non_stimulant_adhd",
    display_name: "Non-Stimulant ADHD Medication",
    description: "Non-stimulant ADHD treatments",
    clinical_tags: ["ADHD", "non-controlled"],
    rebuttal_tags: ["non-stimulant ADHD therapy trialed"],
    step_therapy_level: 2,
    category: "psychiatric",
  },
  hypnotic: {
    id: "hypnotic",
    display_name: "Hypnotic/Sleep Aid",
    description: "Sleep medications",
    clinical_tags: ["insomnia", "sleep disorder"],
    rebuttal_tags: ["sleep medication trialed"],
    step_therapy_level: 1,
    category: "psychiatric",
  },

  // CARDIOVASCULAR
  ace_inhibitor: {
    id: "ace_inhibitor",
    display_name: "ACE Inhibitor",
    description: "Angiotensin-converting enzyme inhibitors",
    clinical_tags: [
      "first-line hypertension",
      "heart failure",
      "renal protection",
    ],
    rebuttal_tags: ["ACE inhibitor therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  arb: {
    id: "arb",
    display_name: "ARB",
    description: "Angiotensin receptor blockers",
    clinical_tags: ["hypertension", "ACE-intolerant", "heart failure"],
    rebuttal_tags: ["ARB therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  beta_blocker: {
    id: "beta_blocker",
    display_name: "Beta Blocker",
    description: "Beta-adrenergic blockers",
    clinical_tags: ["hypertension", "heart failure", "rate control"],
    rebuttal_tags: ["beta blocker therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  calcium_channel_blocker: {
    id: "calcium_channel_blocker",
    display_name: "Calcium Channel Blocker",
    description: "Calcium channel blocking agents",
    clinical_tags: ["hypertension", "angina", "rate control"],
    rebuttal_tags: ["calcium channel blocker therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  diuretic_thiazide: {
    id: "diuretic_thiazide",
    display_name: "Thiazide Diuretic",
    description: "Thiazide and thiazide-like diuretics",
    clinical_tags: ["first-line hypertension", "edema"],
    rebuttal_tags: ["thiazide diuretic therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  diuretic_loop: {
    id: "diuretic_loop",
    display_name: "Loop Diuretic",
    description: "Loop diuretics",
    clinical_tags: ["heart failure", "edema", "renal disease"],
    rebuttal_tags: ["loop diuretic therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  diuretic_potassium_sparing: {
    id: "diuretic_potassium_sparing",
    display_name: "Potassium-Sparing Diuretic",
    description: "Potassium-sparing diuretics",
    clinical_tags: ["heart failure", "hypertension adjunct"],
    rebuttal_tags: ["potassium-sparing diuretic therapy established"],
    step_therapy_level: 2,
    category: "cardiovascular",
  },
  statin: {
    id: "statin",
    display_name: "Statin",
    description: "HMG-CoA reductase inhibitors",
    clinical_tags: ["first-line cholesterol", "cardiovascular prevention"],
    rebuttal_tags: ["statin therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  non_statin_lipid: {
    id: "non_statin_lipid",
    display_name: "Non-Statin Lipid Agent",
    description: "Ezetimibe, fibrates, niacin, omega-3s",
    clinical_tags: ["cholesterol adjunct", "statin-intolerant"],
    rebuttal_tags: ["non-statin lipid therapy trialed"],
    step_therapy_level: 2,
    category: "cardiovascular",
  },
  pcsk9_inhibitor: {
    id: "pcsk9_inhibitor",
    display_name: "PCSK9 Inhibitor",
    description: "PCSK9 inhibitors for severe hyperlipidemia",
    clinical_tags: ["biologic", "refractory hyperlipidemia", "specialty"],
    rebuttal_tags: ["PCSK9 inhibitor therapy", "biologic lipid therapy"],
    step_therapy_level: 3,
    category: "cardiovascular",
  },
  anticoagulant: {
    id: "anticoagulant",
    display_name: "Anticoagulant",
    description: "Blood thinners (warfarin, DOACs, heparins)",
    clinical_tags: ["thrombosis prevention", "stroke prevention"],
    rebuttal_tags: ["anticoagulation therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  doac: {
    id: "doac",
    display_name: "DOAC",
    description: "Direct oral anticoagulants",
    clinical_tags: ["anticoagulation", "afib", "dvt/pe"],
    rebuttal_tags: ["DOAC therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  antiplatelet: {
    id: "antiplatelet",
    display_name: "Antiplatelet",
    description: "Antiplatelet agents",
    clinical_tags: ["cardiovascular prevention", "stroke prevention"],
    rebuttal_tags: ["antiplatelet therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  antiarrhythmic: {
    id: "antiarrhythmic",
    display_name: "Antiarrhythmic",
    description: "Antiarrhythmic medications",
    clinical_tags: ["rhythm control", "arrhythmia"],
    rebuttal_tags: ["antiarrhythmic therapy established"],
    step_therapy_level: 2,
    category: "cardiovascular",
  },
  nitrate: {
    id: "nitrate",
    display_name: "Nitrate",
    description: "Nitrate vasodilators",
    clinical_tags: ["angina", "heart failure"],
    rebuttal_tags: ["nitrate therapy established"],
    step_therapy_level: 1,
    category: "cardiovascular",
  },
  sglt2_inhibitor: {
    id: "sglt2_inhibitor",
    display_name: "SGLT2 Inhibitor",
    description: "Sodium-glucose cotransporter-2 inhibitors",
    clinical_tags: ["diabetes", "heart failure", "renal protection"],
    rebuttal_tags: ["SGLT2 inhibitor therapy established"],
    step_therapy_level: 2,
    category: "cardiovascular",
  },
  arni: {
    id: "arni",
    display_name: "ARNI",
    description: "Angiotensin receptor-neprilysin inhibitors",
    clinical_tags: ["heart failure", "guideline-directed therapy"],
    rebuttal_tags: ["ARNI therapy established for heart failure"],
    step_therapy_level: 2,
    category: "cardiovascular",
  },

  // DIABETES
  metformin: {
    id: "metformin",
    display_name: "Metformin",
    description: "Biguanide antidiabetic",
    clinical_tags: ["first-line diabetes", "insulin sensitizer"],
    rebuttal_tags: ["first-line diabetes therapy established"],
    step_therapy_level: 1,
    category: "diabetes",
  },
  sulfonylurea: {
    id: "sulfonylurea",
    display_name: "Sulfonylurea",
    description: "Sulfonylurea insulin secretagogues",
    clinical_tags: ["diabetes", "insulin secretion"],
    rebuttal_tags: ["sulfonylurea therapy trialed"],
    step_therapy_level: 1,
    category: "diabetes",
  },
  dpp4_inhibitor: {
    id: "dpp4_inhibitor",
    display_name: "DPP-4 Inhibitor",
    description: "Dipeptidyl peptidase-4 inhibitors",
    clinical_tags: ["diabetes", "oral agent"],
    rebuttal_tags: ["DPP-4 inhibitor therapy trialed"],
    step_therapy_level: 2,
    category: "diabetes",
  },
  glp1_agonist: {
    id: "glp1_agonist",
    display_name: "GLP-1 Agonist",
    description: "Glucagon-like peptide-1 receptor agonists",
    clinical_tags: ["diabetes", "weight loss", "cardiovascular benefit"],
    rebuttal_tags: ["GLP-1 agonist therapy established"],
    step_therapy_level: 2,
    category: "diabetes",
  },
  insulin_basal: {
    id: "insulin_basal",
    display_name: "Basal Insulin",
    description: "Long-acting basal insulins",
    clinical_tags: ["diabetes", "insulin therapy"],
    rebuttal_tags: ["basal insulin therapy established"],
    step_therapy_level: 2,
    category: "diabetes",
  },
  insulin_bolus: {
    id: "insulin_bolus",
    display_name: "Rapid-Acting Insulin",
    description: "Rapid-acting mealtime insulins",
    clinical_tags: ["diabetes", "prandial coverage"],
    rebuttal_tags: ["mealtime insulin therapy established"],
    step_therapy_level: 2,
    category: "diabetes",
  },
  insulin_mixed: {
    id: "insulin_mixed",
    display_name: "Mixed Insulin",
    description: "Premixed insulin combinations",
    clinical_tags: ["diabetes", "insulin therapy"],
    rebuttal_tags: ["mixed insulin therapy established"],
    step_therapy_level: 2,
    category: "diabetes",
  },
  thiazolidinedione: {
    id: "thiazolidinedione",
    display_name: "Thiazolidinedione",
    description: "TZD insulin sensitizers",
    clinical_tags: ["diabetes", "insulin sensitizer"],
    rebuttal_tags: ["TZD therapy trialed"],
    step_therapy_level: 2,
    category: "diabetes",
  },

  // RESPIRATORY
  inhaled_saba: {
    id: "inhaled_saba",
    display_name: "Short-Acting Beta Agonist",
    description: "Rescue inhalers (albuterol, levalbuterol)",
    clinical_tags: ["rescue medication", "asthma", "COPD"],
    rebuttal_tags: ["rescue inhaler prescribed"],
    step_therapy_level: 1,
    category: "respiratory",
  },
  inhaled_laba: {
    id: "inhaled_laba",
    display_name: "Long-Acting Beta Agonist",
    description: "LABA controller inhalers",
    clinical_tags: ["controller medication", "COPD", "asthma maintenance"],
    rebuttal_tags: ["LABA therapy established"],
    step_therapy_level: 2,
    category: "respiratory",
  },
  inhaled_ics: {
    id: "inhaled_ics",
    display_name: "Inhaled Corticosteroid",
    description: "ICS controller inhalers",
    clinical_tags: ["first-line controller", "asthma", "anti-inflammatory"],
    rebuttal_tags: ["inhaled corticosteroid therapy established"],
    step_therapy_level: 1,
    category: "respiratory",
  },
  inhaled_ics_laba: {
    id: "inhaled_ics_laba",
    display_name: "ICS/LABA Combination",
    description: "Combination corticosteroid/LABA inhalers",
    clinical_tags: ["controller medication", "step-up therapy"],
    rebuttal_tags: ["combination controller inhaler therapy established"],
    step_therapy_level: 2,
    category: "respiratory",
  },
  inhaled_lama: {
    id: "inhaled_lama",
    display_name: "Long-Acting Muscarinic Antagonist",
    description: "LAMA bronchodilators",
    clinical_tags: ["COPD first-line", "bronchodilator"],
    rebuttal_tags: ["LAMA therapy established"],
    step_therapy_level: 1,
    category: "respiratory",
  },
  inhaled_triple: {
    id: "inhaled_triple",
    display_name: "Triple Therapy Inhaler",
    description: "ICS/LABA/LAMA combination",
    clinical_tags: ["COPD", "severe asthma", "triple therapy"],
    rebuttal_tags: ["triple inhaler therapy established"],
    step_therapy_level: 3,
    category: "respiratory",
  },
  leukotriene_modifier: {
    id: "leukotriene_modifier",
    display_name: "Leukotriene Modifier",
    description: "Leukotriene receptor antagonists",
    clinical_tags: ["asthma adjunct", "allergic rhinitis"],
    rebuttal_tags: ["leukotriene modifier therapy trialed"],
    step_therapy_level: 1,
    category: "respiratory",
  },
  biologic_asthma: {
    id: "biologic_asthma",
    display_name: "Biologic for Asthma",
    description: "Biologic agents for severe asthma",
    clinical_tags: ["biologic", "severe asthma", "specialty"],
    rebuttal_tags: [
      "biologic therapy for asthma",
      "specialty therapy required",
    ],
    step_therapy_level: 3,
    category: "respiratory",
  },
  cftr_modulator: {
    id: "cftr_modulator",
    display_name: "CFTR Modulator",
    description:
      "Cystic fibrosis transmembrane conductance regulator modulators",
    clinical_tags: ["cystic fibrosis", "specialty", "genotype-specific"],
    rebuttal_tags: [
      "CFTR modulator therapy required",
      "genotype-confirmed eligibility",
    ],
    step_therapy_level: 3,
    category: "respiratory",
  },
  antifibrotic_pulmonary: {
    id: "antifibrotic_pulmonary",
    display_name: "Pulmonary Antifibrotic",
    description:
      "Antifibrotic agents for IPF and progressive pulmonary fibrosis",
    clinical_tags: ["IPF", "pulmonary fibrosis", "specialty", "antifibrotic"],
    rebuttal_tags: [
      "antifibrotic therapy indicated",
      "disease progression documented",
    ],
    step_therapy_level: 3,
    category: "respiratory",
  },
  pah_era: {
    id: "pah_era",
    display_name: "Endothelin Receptor Antagonist",
    description: "ERAs for pulmonary arterial hypertension",
    clinical_tags: ["PAH", "pulmonary hypertension", "specialty", "REMS"],
    rebuttal_tags: ["ERA therapy for PAH", "WHO Group I confirmed"],
    step_therapy_level: 2,
    category: "respiratory",
  },
  pah_pde5: {
    id: "pah_pde5",
    display_name: "PDE5 Inhibitor for PAH",
    description: "PDE5 inhibitors used for pulmonary arterial hypertension",
    clinical_tags: ["PAH", "pulmonary hypertension", "first-line oral"],
    rebuttal_tags: ["PDE5 inhibitor for PAH established"],
    step_therapy_level: 1,
    category: "respiratory",
  },
  pah_sgc_stimulator: {
    id: "pah_sgc_stimulator",
    display_name: "Soluble Guanylate Cyclase Stimulator",
    description: "sGC stimulators for PAH and CTEPH",
    clinical_tags: [
      "PAH",
      "CTEPH",
      "pulmonary hypertension",
      "specialty",
      "REMS",
    ],
    rebuttal_tags: ["sGC stimulator therapy indicated"],
    step_therapy_level: 2,
    category: "respiratory",
  },
  pah_prostacyclin: {
    id: "pah_prostacyclin",
    display_name: "Prostacyclin Pathway Agent",
    description: "Prostacyclin analogs and IP receptor agonists for PAH",
    clinical_tags: [
      "PAH",
      "pulmonary hypertension",
      "specialty",
      "advanced therapy",
    ],
    rebuttal_tags: ["prostacyclin therapy required", "advanced PAH therapy"],
    step_therapy_level: 3,
    category: "respiratory",
  },
  alpha1_augmentation: {
    id: "alpha1_augmentation",
    display_name: "Alpha-1 Antitrypsin Augmentation",
    description: "AAT augmentation therapy for alpha-1 antitrypsin deficiency",
    clinical_tags: [
      "alpha-1 antitrypsin deficiency",
      "specialty",
      "weekly infusion",
    ],
    rebuttal_tags: [
      "AAT augmentation therapy indicated",
      "phenotype-confirmed deficiency",
    ],
    step_therapy_level: 3,
    category: "respiratory",
  },
  pde4_inhibitor: {
    id: "pde4_inhibitor",
    display_name: "PDE4 Inhibitor",
    description: "Phosphodiesterase-4 inhibitors for COPD",
    clinical_tags: ["COPD", "chronic bronchitis", "exacerbation reduction"],
    rebuttal_tags: ["PDE4 inhibitor therapy trialed"],
    step_therapy_level: 2,
    category: "respiratory",
  },
  pde3_pde4_inhibitor: {
    id: "pde3_pde4_inhibitor",
    display_name: "PDE3/PDE4 Inhibitor",
    description: "Dual PDE3/PDE4 inhibitors for COPD maintenance",
    clinical_tags: ["COPD", "novel mechanism", "maintenance therapy"],
    rebuttal_tags: ["PDE3/PDE4 inhibitor therapy indicated"],
    step_therapy_level: 3,
    category: "respiratory",
  },
  inhaled_lama_laba: {
    id: "inhaled_lama_laba",
    display_name: "LAMA/LABA Combination",
    description:
      "Long-acting muscarinic antagonist + long-acting beta agonist combination inhalers",
    clinical_tags: ["COPD", "dual bronchodilator", "controller medication"],
    rebuttal_tags: ["LAMA/LABA combination therapy established"],
    step_therapy_level: 2,
    category: "respiratory",
  },
  cf_mucoactive: {
    id: "cf_mucoactive",
    display_name: "Cystic Fibrosis Mucoactive Agent",
    description:
      "Mucolytic and airway clearance agents for CF and bronchiectasis",
    clinical_tags: [
      "cystic fibrosis",
      "bronchiectasis",
      "airway clearance",
      "specialty",
    ],
    rebuttal_tags: [
      "mucoactive therapy required",
      "airway clearance optimization",
    ],
    step_therapy_level: 1,
    category: "respiratory",
  },
  inhaled_antibiotic: {
    id: "inhaled_antibiotic",
    display_name: "Inhaled Antibiotic",
    description: "Inhaled antimicrobials for CF, bronchiectasis, and NTM",
    clinical_tags: [
      "cystic fibrosis",
      "bronchiectasis",
      "NTM",
      "chronic suppression",
      "specialty",
    ],
    rebuttal_tags: [
      "inhaled antibiotic therapy indicated",
      "chronic suppressive therapy required",
    ],
    step_therapy_level: 2,
    category: "respiratory",
  },
  acth_analog: {
    id: "acth_analog",
    display_name: "ACTH Analog",
    description:
      "Repository corticotropin for steroid-refractory inflammatory conditions",
    clinical_tags: [
      "sarcoidosis",
      "steroid alternative",
      "specialty",
      "high-cost",
    ],
    rebuttal_tags: ["ACTH therapy indicated", "steroid failure documented"],
    step_therapy_level: 4,
    category: "respiratory",
  },
  chronic_cough_p2x3: {
    id: "chronic_cough_p2x3",
    display_name: "P2X3 Receptor Antagonist",
    description: "P2X3 antagonists for refractory chronic cough",
    clinical_tags: ["chronic cough", "refractory cough", "novel mechanism"],
    rebuttal_tags: ["refractory chronic cough therapy indicated"],
    step_therapy_level: 3,
    category: "respiratory",
  },
  pah_activin_inhibitor: {
    id: "pah_activin_inhibitor",
    display_name: "Activin Signaling Inhibitor for PAH",
    description: "Activin receptor type IIA-Fc fusion proteins for PAH",
    clinical_tags: [
      "PAH",
      "pulmonary hypertension",
      "specialty",
      "novel mechanism",
      "advanced therapy",
    ],
    rebuttal_tags: [
      "activin signaling inhibitor indicated",
      "advanced PAH therapy",
    ],
    step_therapy_level: 4,
    category: "respiratory",
  },
  smoking_cessation: {
    id: "smoking_cessation",
    display_name: "Smoking Cessation Agent",
    description: "Pharmacotherapy for tobacco use disorder",
    clinical_tags: [
      "smoking cessation",
      "tobacco use disorder",
      "harm reduction",
    ],
    rebuttal_tags: ["smoking cessation therapy indicated"],
    step_therapy_level: 1,
    category: "respiratory",
  },

  // RHEUMATOLOGY/IMMUNOLOGY
  corticosteroid_oral: {
    id: "corticosteroid_oral",
    display_name: "Oral Corticosteroid",
    description: "Systemic corticosteroids",
    clinical_tags: ["anti-inflammatory", "immunosuppressive", "bridge therapy"],
    rebuttal_tags: ["oral corticosteroid therapy documented"],
    step_therapy_level: 1,
    category: "rheumatology",
  },
  corticosteroid_injection: {
    id: "corticosteroid_injection",
    display_name: "Injectable Corticosteroid",
    description: "Corticosteroid injections",
    clinical_tags: [
      "local injection",
      "joint injection",
      "conservative therapy",
    ],
    rebuttal_tags: ["corticosteroid injection therapy documented"],
    step_therapy_level: 1,
    category: "rheumatology",
  },
  dmard_conventional: {
    id: "dmard_conventional",
    display_name: "Conventional DMARD",
    description: "Conventional disease-modifying antirheumatic drugs",
    clinical_tags: ["first-line DMARD", "methotrexate", "disease-modifying"],
    rebuttal_tags: [
      "conventional DMARD therapy trialed",
      "first-line DMARD attempted",
    ],
    step_therapy_level: 1,
    category: "rheumatology",
  },
  biologic_tnf: {
    id: "biologic_tnf",
    display_name: "TNF Inhibitor",
    description: "Anti-TNF biologic agents",
    clinical_tags: ["biologic", "DMARD failure", "specialty"],
    rebuttal_tags: ["TNF inhibitor therapy", "biologic DMARD therapy"],
    step_therapy_level: 2,
    category: "rheumatology",
  },
  biologic_il6: {
    id: "biologic_il6",
    display_name: "IL-6 Inhibitor",
    description: "IL-6 receptor inhibitors",
    clinical_tags: ["biologic", "specialty"],
    rebuttal_tags: ["IL-6 inhibitor therapy"],
    step_therapy_level: 2,
    category: "rheumatology",
  },
  biologic_il17: {
    id: "biologic_il17",
    display_name: "IL-17 Inhibitor",
    description: "IL-17 inhibitors",
    clinical_tags: ["biologic", "psoriasis", "spondyloarthritis"],
    rebuttal_tags: ["IL-17 inhibitor therapy"],
    step_therapy_level: 2,
    category: "rheumatology",
  },
  biologic_il23: {
    id: "biologic_il23",
    display_name: "IL-23 Inhibitor",
    description: "IL-23 inhibitors",
    clinical_tags: ["biologic", "psoriasis", "IBD"],
    rebuttal_tags: ["IL-23 inhibitor therapy"],
    step_therapy_level: 2,
    category: "rheumatology",
  },
  biologic_il12_23: {
    id: "biologic_il12_23",
    display_name: "IL-12/23 Inhibitor",
    description: "IL-12/23 inhibitors (ustekinumab)",
    clinical_tags: ["biologic", "psoriasis", "IBD"],
    rebuttal_tags: ["IL-12/23 inhibitor therapy"],
    step_therapy_level: 2,
    category: "rheumatology",
  },
  jak_inhibitor: {
    id: "jak_inhibitor",
    display_name: "JAK Inhibitor",
    description: "Janus kinase inhibitors",
    clinical_tags: ["targeted synthetic DMARD", "oral biologic-equivalent"],
    rebuttal_tags: ["JAK inhibitor therapy trialed"],
    step_therapy_level: 2,
    category: "rheumatology",
  },
  biologic_b_cell: {
    id: "biologic_b_cell",
    display_name: "B-Cell Targeted Therapy",
    description: "B-cell depleting agents (rituximab)",
    clinical_tags: ["biologic", "B-cell depletion"],
    rebuttal_tags: ["B-cell targeted therapy"],
    step_therapy_level: 3,
    category: "rheumatology",
  },
  biologic_t_cell: {
    id: "biologic_t_cell",
    display_name: "T-Cell Modulator",
    description: "T-cell costimulation modulators",
    clinical_tags: ["biologic", "T-cell modulation"],
    rebuttal_tags: ["T-cell modulator therapy"],
    step_therapy_level: 2,
    category: "rheumatology",
  },
  colchicine: {
    id: "colchicine",
    display_name: "Colchicine",
    description: "Anti-inflammatory for gout",
    clinical_tags: ["gout", "pericarditis", "anti-inflammatory"],
    rebuttal_tags: ["colchicine therapy trialed"],
    step_therapy_level: 1,
    category: "rheumatology",
  },
  urate_lowering: {
    id: "urate_lowering",
    display_name: "Urate-Lowering Agent",
    description: "Gout prophylaxis",
    clinical_tags: ["gout prophylaxis", "uric acid lowering"],
    rebuttal_tags: ["urate-lowering therapy established"],
    step_therapy_level: 1,
    category: "rheumatology",
  },

  // GI
  ppi: {
    id: "ppi",
    display_name: "Proton Pump Inhibitor",
    description: "PPIs for acid suppression",
    clinical_tags: ["first-line GERD", "acid suppression"],
    rebuttal_tags: ["PPI therapy established"],
    step_therapy_level: 1,
    category: "gi",
  },
  h2_blocker: {
    id: "h2_blocker",
    display_name: "H2 Blocker",
    description: "H2 receptor antagonists",
    clinical_tags: ["acid suppression", "GERD adjunct"],
    rebuttal_tags: ["H2 blocker therapy trialed"],
    step_therapy_level: 1,
    category: "gi",
  },
  prokinetic: {
    id: "prokinetic",
    display_name: "Prokinetic Agent",
    description: "GI motility agents",
    clinical_tags: ["gastroparesis", "motility"],
    rebuttal_tags: ["prokinetic therapy trialed"],
    step_therapy_level: 1,
    category: "gi",
  },
  antispasmodic: {
    id: "antispasmodic",
    display_name: "Antispasmodic",
    description: "GI antispasmodics",
    clinical_tags: ["IBS", "abdominal cramping"],
    rebuttal_tags: ["antispasmodic therapy trialed"],
    step_therapy_level: 1,
    category: "gi",
  },
  laxative: {
    id: "laxative",
    display_name: "Laxative",
    description: "Laxatives and stool softeners",
    clinical_tags: ["constipation", "bowel regimen"],
    rebuttal_tags: ["laxative therapy trialed"],
    step_therapy_level: 1,
    category: "gi",
  },
  antidiarrheal: {
    id: "antidiarrheal",
    display_name: "Antidiarrheal",
    description: "Antidiarrheal agents",
    clinical_tags: ["diarrhea", "IBS-D"],
    rebuttal_tags: ["antidiarrheal therapy trialed"],
    step_therapy_level: 1,
    category: "gi",
  },
  aminosalicylate: {
    id: "aminosalicylate",
    display_name: "Aminosalicylate",
    description: "5-ASA agents for IBD",
    clinical_tags: ["IBD", "ulcerative colitis", "first-line"],
    rebuttal_tags: ["aminosalicylate therapy established"],
    step_therapy_level: 1,
    category: "gi",
  },
  antiemetic: {
    id: "antiemetic",
    display_name: "Antiemetic",
    description: "Anti-nausea medications",
    clinical_tags: ["nausea", "vomiting", "chemotherapy support"],
    rebuttal_tags: ["antiemetic therapy trialed"],
    step_therapy_level: 1,
    category: "gi",
  },

  // NEUROLOGY
  anticonvulsant: {
    id: "anticonvulsant",
    display_name: "Anticonvulsant",
    description: "Antiepileptic drugs",
    clinical_tags: ["epilepsy", "seizure prevention", "neuropathic pain"],
    rebuttal_tags: ["anticonvulsant therapy established"],
    step_therapy_level: 1,
    category: "neurology",
  },
  triptan: {
    id: "triptan",
    display_name: "Triptan",
    description: "Serotonin receptor agonists for migraine",
    clinical_tags: ["migraine abortive", "first-line migraine"],
    rebuttal_tags: ["triptan therapy trialed for migraine"],
    step_therapy_level: 1,
    category: "neurology",
  },
  cgrp_inhibitor: {
    id: "cgrp_inhibitor",
    display_name: "CGRP Inhibitor",
    description: "CGRP antagonists for migraine prevention",
    clinical_tags: ["migraine prevention", "biologic", "specialty"],
    rebuttal_tags: ["CGRP inhibitor therapy", "migraine biologic therapy"],
    step_therapy_level: 2,
    category: "neurology",
  },
  migraine_preventive: {
    id: "migraine_preventive",
    display_name: "Migraine Preventive",
    description: "Traditional migraine prophylaxis",
    clinical_tags: ["migraine prevention", "prophylaxis"],
    rebuttal_tags: ["migraine preventive therapy trialed"],
    step_therapy_level: 1,
    category: "neurology",
  },
  parkinsons_dopamine: {
    id: "parkinsons_dopamine",
    display_name: "Dopaminergic Agent",
    description: "Dopamine agonists and levodopa",
    clinical_tags: ["Parkinson's disease", "dopamine replacement"],
    rebuttal_tags: ["dopaminergic therapy established"],
    step_therapy_level: 1,
    category: "neurology",
  },
  ms_disease_modifying: {
    id: "ms_disease_modifying",
    display_name: "MS Disease-Modifying Therapy",
    description: "Disease-modifying therapies for MS",
    clinical_tags: ["multiple sclerosis", "DMT", "specialty"],
    rebuttal_tags: ["MS disease-modifying therapy established"],
    step_therapy_level: 2,
    category: "neurology",
  },
  alzheimers: {
    id: "alzheimers",
    display_name: "Alzheimer's Medication",
    description: "Cholinesterase inhibitors and memantine",
    clinical_tags: ["dementia", "cognitive support"],
    rebuttal_tags: ["Alzheimer's therapy established"],
    step_therapy_level: 1,
    category: "neurology",
  },

  // ALLERGY/IMMUNOLOGY
  antihistamine: {
    id: "antihistamine",
    display_name: "Antihistamine",
    description: "H1 antihistamines",
    clinical_tags: ["allergies", "urticaria", "first-line"],
    rebuttal_tags: ["antihistamine therapy trialed"],
    step_therapy_level: 1,
    category: "allergy",
  },
  nasal_steroid: {
    id: "nasal_steroid",
    display_name: "Nasal Corticosteroid",
    description: "Intranasal corticosteroids",
    clinical_tags: ["allergic rhinitis", "first-line"],
    rebuttal_tags: ["nasal corticosteroid therapy trialed"],
    step_therapy_level: 1,
    category: "allergy",
  },
  nasal_antihistamine: {
    id: "nasal_antihistamine",
    display_name: "Nasal Antihistamine",
    description: "Intranasal antihistamines",
    clinical_tags: ["allergic rhinitis"],
    rebuttal_tags: ["nasal antihistamine therapy trialed"],
    step_therapy_level: 1,
    category: "allergy",
  },
  epinephrine: {
    id: "epinephrine",
    display_name: "Epinephrine Auto-Injector",
    description: "Emergency epinephrine",
    clinical_tags: ["anaphylaxis", "emergency medication"],
    rebuttal_tags: ["epinephrine auto-injector prescribed"],
    step_therapy_level: 1,
    category: "allergy",
  },

  // DERMATOLOGY
  topical_steroid: {
    id: "topical_steroid",
    display_name: "Topical Corticosteroid",
    description: "Topical corticosteroids",
    clinical_tags: ["first-line dermatitis", "anti-inflammatory"],
    rebuttal_tags: ["topical corticosteroid therapy trialed"],
    step_therapy_level: 1,
    category: "dermatology",
  },
  topical_calcineurin: {
    id: "topical_calcineurin",
    display_name: "Topical Calcineurin Inhibitor",
    description: "Tacrolimus and pimecrolimus",
    clinical_tags: ["atopic dermatitis", "steroid-sparing"],
    rebuttal_tags: ["topical calcineurin inhibitor therapy trialed"],
    step_therapy_level: 2,
    category: "dermatology",
  },
  topical_retinoid: {
    id: "topical_retinoid",
    display_name: "Topical Retinoid",
    description: "Topical vitamin A derivatives",
    clinical_tags: ["acne", "photoaging"],
    rebuttal_tags: ["topical retinoid therapy trialed"],
    step_therapy_level: 1,
    category: "dermatology",
  },
  oral_retinoid: {
    id: "oral_retinoid",
    display_name: "Oral Retinoid",
    description: "Systemic retinoids",
    clinical_tags: ["severe acne", "psoriasis", "specialty"],
    rebuttal_tags: ["oral retinoid therapy trialed"],
    step_therapy_level: 2,
    category: "dermatology",
  },
  biologic_dermatology: {
    id: "biologic_dermatology",
    display_name: "Biologic for Skin Disease",
    description: "Biologics for psoriasis, eczema",
    clinical_tags: ["biologic", "specialty", "severe skin disease"],
    rebuttal_tags: ["biologic therapy for skin disease"],
    step_therapy_level: 3,
    category: "dermatology",
  },
  antifungal_topical: {
    id: "antifungal_topical",
    display_name: "Topical Antifungal",
    description: "Topical antifungal agents",
    clinical_tags: ["fungal infection", "first-line"],
    rebuttal_tags: ["topical antifungal therapy trialed"],
    step_therapy_level: 1,
    category: "dermatology",
  },
  antifungal_oral: {
    id: "antifungal_oral",
    display_name: "Oral Antifungal",
    description: "Systemic antifungal agents",
    clinical_tags: ["fungal infection", "onychomycosis"],
    rebuttal_tags: ["oral antifungal therapy trialed"],
    step_therapy_level: 2,
    category: "dermatology",
  },
  topical_rosacea: {
    id: "topical_rosacea",
    display_name: "Topical Rosacea Agent",
    description: "Topical treatments for rosacea",
    clinical_tags: ["rosacea", "facial erythema"],
    rebuttal_tags: ["topical rosacea therapy trialed"],
    step_therapy_level: 1,
    category: "dermatology",
  },
  topical_acne: {
    id: "topical_acne",
    display_name: "Topical Acne Agent",
    description: "Topical treatments for acne vulgaris",
    clinical_tags: ["acne", "comedonal", "inflammatory"],
    rebuttal_tags: ["topical acne therapy trialed"],
    step_therapy_level: 1,
    category: "dermatology",
  },
  hormonal_acne: {
    id: "hormonal_acne",
    display_name: "Hormonal Acne Therapy",
    description: "Hormonal agents for acne management",
    clinical_tags: ["hormonal acne", "antiandrogen"],
    rebuttal_tags: ["hormonal acne therapy trialed"],
    step_therapy_level: 2,
    category: "dermatology",
  },
  topical_immune_modifier: {
    id: "topical_immune_modifier",
    display_name: "Topical Immune Modifier",
    description: "Topical immune response modifiers for skin lesions",
    clinical_tags: ["actinic keratosis", "basal cell carcinoma", "warts"],
    rebuttal_tags: ["topical immune modifier therapy trialed"],
    step_therapy_level: 2,
    category: "dermatology",
  },
  topical_hyperhidrosis: {
    id: "topical_hyperhidrosis",
    display_name: "Topical Hyperhidrosis Agent",
    description: "Topical anticholinergic agents for excessive sweating",
    clinical_tags: ["hyperhidrosis", "excessive sweating"],
    rebuttal_tags: ["topical hyperhidrosis therapy trialed"],
    step_therapy_level: 1,
    category: "dermatology",
  },
  immunosuppressant_derm: {
    id: "immunosuppressant_derm",
    display_name: "Immunosuppressant (Dermatologic)",
    description: "Systemic immunosuppressants for dermatologic conditions",
    clinical_tags: [
      "autoimmune skin disease",
      "pemphigus",
      "lupus skin",
      "dermatomyositis",
    ],
    rebuttal_tags: ["systemic immunosuppressant therapy trialed"],
    step_therapy_level: 3,
    category: "dermatology",
  },

  // INFECTIOUS DISEASE
  antibiotic: {
    id: "antibiotic",
    display_name: "Antibiotic",
    description: "Antibacterial agents",
    clinical_tags: ["infection treatment"],
    rebuttal_tags: ["antibiotic therapy documented"],
    step_therapy_level: 1,
    category: "infectious",
  },
  antiviral: {
    id: "antiviral",
    display_name: "Antiviral",
    description: "Antiviral medications",
    clinical_tags: ["viral infection"],
    rebuttal_tags: ["antiviral therapy documented"],
    step_therapy_level: 1,
    category: "infectious",
  },
  antiretroviral: {
    id: "antiretroviral",
    display_name: "Antiretroviral",
    description: "HIV antiretroviral therapy",
    clinical_tags: ["HIV treatment", "specialty"],
    rebuttal_tags: ["antiretroviral therapy established"],
    step_therapy_level: 1,
    category: "infectious",
  },
  hep_c_daa: {
    id: "hep_c_daa",
    display_name: "Hepatitis C DAA",
    description: "Direct-acting antivirals for hepatitis C",
    clinical_tags: ["hepatitis C", "curative therapy", "specialty"],
    rebuttal_tags: ["hepatitis C DAA therapy"],
    step_therapy_level: 1,
    category: "infectious",
  },

  // UROLOGY
  alpha_blocker_uro: {
    id: "alpha_blocker_uro",
    display_name: "Alpha Blocker (Urologic)",
    description: "Alpha blockers for BPH",
    clinical_tags: ["BPH", "first-line urologic"],
    rebuttal_tags: ["alpha blocker therapy established for BPH"],
    step_therapy_level: 1,
    category: "urology",
  },
  five_alpha_reductase: {
    id: "five_alpha_reductase",
    display_name: "5-Alpha Reductase Inhibitor",
    description: "Finasteride and dutasteride",
    clinical_tags: ["BPH", "prostate size reduction"],
    rebuttal_tags: ["5-alpha reductase inhibitor therapy established"],
    step_therapy_level: 1,
    category: "urology",
  },
  overactive_bladder: {
    id: "overactive_bladder",
    display_name: "Overactive Bladder Medication",
    description: "Antimuscarinics and beta-3 agonists",
    clinical_tags: ["overactive bladder", "urge incontinence"],
    rebuttal_tags: ["overactive bladder medication trialed"],
    step_therapy_level: 1,
    category: "urology",
  },
  pde5_inhibitor: {
    id: "pde5_inhibitor",
    display_name: "PDE5 Inhibitor",
    description: "Phosphodiesterase-5 inhibitors",
    clinical_tags: ["erectile dysfunction", "pulmonary hypertension"],
    rebuttal_tags: ["PDE5 inhibitor therapy established"],
    step_therapy_level: 1,
    category: "urology",
  },

  // ENDOCRINE
  thyroid_replacement: {
    id: "thyroid_replacement",
    display_name: "Thyroid Replacement",
    description: "Levothyroxine and liothyronine",
    clinical_tags: ["hypothyroidism", "hormone replacement"],
    rebuttal_tags: ["thyroid replacement therapy established"],
    step_therapy_level: 1,
    category: "endocrine",
  },
  antithyroid: {
    id: "antithyroid",
    display_name: "Antithyroid Agent",
    description: "Methimazole and propylthiouracil",
    clinical_tags: ["hyperthyroidism"],
    rebuttal_tags: ["antithyroid therapy established"],
    step_therapy_level: 1,
    category: "endocrine",
  },
  bisphosphonate: {
    id: "bisphosphonate",
    display_name: "Bisphosphonate",
    description: "Bisphosphonates for osteoporosis",
    clinical_tags: ["osteoporosis", "first-line bone therapy"],
    rebuttal_tags: ["bisphosphonate therapy established"],
    step_therapy_level: 1,
    category: "endocrine",
  },
  denosumab: {
    id: "denosumab",
    display_name: "Denosumab",
    description: "RANKL inhibitor for osteoporosis",
    clinical_tags: ["osteoporosis", "biologic bone therapy"],
    rebuttal_tags: ["denosumab therapy established"],
    step_therapy_level: 2,
    category: "endocrine",
  },
  teriparatide: {
    id: "teriparatide",
    display_name: "Anabolic Bone Agent",
    description: "PTH analogs for severe osteoporosis",
    clinical_tags: ["severe osteoporosis", "anabolic therapy"],
    rebuttal_tags: ["anabolic bone therapy established"],
    step_therapy_level: 3,
    category: "endocrine",
  },
  testosterone: {
    id: "testosterone",
    display_name: "Testosterone",
    description: "Testosterone replacement therapy",
    clinical_tags: ["hypogonadism", "hormone replacement"],
    rebuttal_tags: ["testosterone replacement therapy established"],
    step_therapy_level: 1,
    category: "endocrine",
  },
  estrogen: {
    id: "estrogen",
    display_name: "Estrogen Therapy",
    description: "Estrogen combinations",
    clinical_tags: ["menopause", "hormone replacement"],
    rebuttal_tags: ["estrogen therapy established"],
    step_therapy_level: 1,
    category: "endocrine",
  },
  growth_hormone: {
    id: "growth_hormone",
    display_name: "Growth Hormone",
    description: "Somatropin",
    clinical_tags: ["growth hormone deficiency", "specialty"],
    rebuttal_tags: ["growth hormone therapy established"],
    step_therapy_level: 2,
    category: "endocrine",
  },

  // ONCOLOGY
  chemotherapy: {
    id: "chemotherapy",
    display_name: "Chemotherapy",
    description: "Cytotoxic chemotherapy agents",
    clinical_tags: ["cancer treatment", "cytotoxic"],
    rebuttal_tags: ["chemotherapy regimen established"],
    step_therapy_level: 1,
    category: "oncology",
  },
  targeted_oncology: {
    id: "targeted_oncology",
    display_name: "Targeted Cancer Therapy",
    description: "Targeted molecular agents",
    clinical_tags: ["cancer treatment", "targeted therapy"],
    rebuttal_tags: ["targeted cancer therapy established"],
    step_therapy_level: 2,
    category: "oncology",
  },
  immunotherapy_oncology: {
    id: "immunotherapy_oncology",
    display_name: "Cancer Immunotherapy",
    description: "Checkpoint inhibitors",
    clinical_tags: ["cancer treatment", "immunotherapy"],
    rebuttal_tags: ["cancer immunotherapy established"],
    step_therapy_level: 2,
    category: "oncology",
  },
  hormonal_oncology: {
    id: "hormonal_oncology",
    display_name: "Hormonal Cancer Therapy",
    description: "Hormone-based cancer treatments",
    clinical_tags: ["cancer treatment", "hormonal therapy"],
    rebuttal_tags: ["hormonal cancer therapy established"],
    step_therapy_level: 1,
    category: "oncology",
  },
  supportive_oncology: {
    id: "supportive_oncology",
    display_name: "Oncology Supportive Care",
    description: "Growth factors, antiemetics, bone agents",
    clinical_tags: ["supportive care", "cancer support"],
    rebuttal_tags: ["oncology supportive care documented"],
    step_therapy_level: 1,
    category: "oncology",
  },

  // MISCELLANEOUS
  vitamin_supplement: {
    id: "vitamin_supplement",
    display_name: "Vitamin/Supplement",
    description: "Vitamins and nutritional supplements",
    clinical_tags: ["nutritional support", "deficiency treatment"],
    rebuttal_tags: ["vitamin/supplement therapy documented"],
    step_therapy_level: 1,
    category: "misc",
  },
  misc_other: {
    id: "misc_other",
    display_name: "Other Medication",
    description: "Medications not fitting other categories",
    clinical_tags: ["general"],
    rebuttal_tags: ["medication therapy documented"],
    step_therapy_level: 1,
    category: "misc",
  },
};

// ============================================
// LAYER 1: MEDICATION DATABASE (Condensed)
// ============================================
// Common medications with class mappings - supports 500+ medications

export const MEDICATIONS: Medication[] = [
  // PAIN - NSAIDs
  {
    name: "ibuprofen",
    generic_name: "ibuprofen",
    brand_names: ["Advil", "Motrin"],
    class_id: "nsaid",
    synonyms: ["ibu"],
    route: "oral",
    common: true,
  },
  {
    name: "naproxen",
    generic_name: "naproxen",
    brand_names: ["Aleve", "Naprosyn"],
    class_id: "nsaid",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "meloxicam",
    generic_name: "meloxicam",
    brand_names: ["Mobic"],
    class_id: "nsaid",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "celecoxib",
    generic_name: "celecoxib",
    brand_names: ["Celebrex"],
    class_id: "nsaid",
    synonyms: ["cox-2"],
    route: "oral",
    common: true,
  },
  {
    name: "diclofenac",
    generic_name: "diclofenac",
    brand_names: ["Voltaren", "Cataflam"],
    class_id: "nsaid",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "indomethacin",
    generic_name: "indomethacin",
    brand_names: ["Indocin"],
    class_id: "nsaid",
    synonyms: ["indo"],
    route: "oral",
  },
  {
    name: "ketorolac",
    generic_name: "ketorolac",
    brand_names: ["Toradol"],
    class_id: "nsaid",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "aspirin",
    generic_name: "aspirin",
    brand_names: ["Bayer", "Ecotrin"],
    class_id: "nsaid",
    synonyms: ["asa"],
    route: "oral",
    common: true,
  },

  // PAIN - Acetaminophen
  {
    name: "acetaminophen",
    generic_name: "acetaminophen",
    brand_names: ["Tylenol"],
    class_id: "acetaminophen",
    synonyms: ["apap", "paracetamol"],
    route: "oral",
    common: true,
  },

  // PAIN - Topical
  {
    name: "lidocaine patch",
    generic_name: "lidocaine",
    brand_names: ["Lidoderm"],
    class_id: "topical_analgesic",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "diclofenac gel",
    generic_name: "diclofenac topical",
    brand_names: ["Voltaren Gel"],
    class_id: "topical_analgesic",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "capsaicin",
    generic_name: "capsaicin",
    brand_names: ["Zostrix", "Qutenza"],
    class_id: "topical_analgesic",
    synonyms: [],
    route: "topical",
  },

  // PAIN - Muscle Relaxants
  {
    name: "cyclobenzaprine",
    generic_name: "cyclobenzaprine",
    brand_names: ["Flexeril"],
    class_id: "muscle_relaxant",
    synonyms: ["flexeril"],
    route: "oral",
    common: true,
  },
  {
    name: "methocarbamol",
    generic_name: "methocarbamol",
    brand_names: ["Robaxin"],
    class_id: "muscle_relaxant",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "tizanidine",
    generic_name: "tizanidine",
    brand_names: ["Zanaflex"],
    class_id: "muscle_relaxant",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "baclofen",
    generic_name: "baclofen",
    brand_names: ["Lioresal"],
    class_id: "muscle_relaxant",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "carisoprodol",
    generic_name: "carisoprodol",
    brand_names: ["Soma"],
    class_id: "muscle_relaxant",
    synonyms: [],
    route: "oral",
    controlled: true,
  },
  {
    name: "metaxalone",
    generic_name: "metaxalone",
    brand_names: ["Skelaxin"],
    class_id: "muscle_relaxant",
    synonyms: [],
    route: "oral",
  },

  // PAIN - Gabapentinoids
  {
    name: "gabapentin",
    generic_name: "gabapentin",
    brand_names: ["Neurontin"],
    class_id: "gabapentinoid",
    synonyms: ["gaba"],
    route: "oral",
    common: true,
  },
  {
    name: "pregabalin",
    generic_name: "pregabalin",
    brand_names: ["Lyrica"],
    class_id: "gabapentinoid",
    synonyms: [],
    route: "oral",
    common: true,
    controlled: true,
  },

  // PAIN - Opioids Short-Acting
  {
    name: "hydrocodone/acetaminophen",
    generic_name: "hydrocodone/acetaminophen",
    brand_names: ["Vicodin", "Norco"],
    class_id: "opioid_short_acting",
    synonyms: ["vicodin", "norco"],
    route: "oral",
    controlled: true,
    common: true,
  },
  {
    name: "oxycodone",
    generic_name: "oxycodone",
    brand_names: ["Roxicodone"],
    class_id: "opioid_short_acting",
    synonyms: ["oxy"],
    route: "oral",
    controlled: true,
    common: true,
  },
  {
    name: "oxycodone/acetaminophen",
    generic_name: "oxycodone/acetaminophen",
    brand_names: ["Percocet"],
    class_id: "opioid_short_acting",
    synonyms: ["percocet"],
    route: "oral",
    controlled: true,
    common: true,
  },
  {
    name: "tramadol",
    generic_name: "tramadol",
    brand_names: ["Ultram"],
    class_id: "opioid_short_acting",
    synonyms: [],
    route: "oral",
    controlled: true,
    common: true,
  },
  {
    name: "codeine",
    generic_name: "codeine",
    brand_names: ["Tylenol #3"],
    class_id: "opioid_short_acting",
    synonyms: [],
    route: "oral",
    controlled: true,
  },
  {
    name: "morphine IR",
    generic_name: "morphine immediate-release",
    brand_names: ["MSIR"],
    class_id: "opioid_short_acting",
    synonyms: [],
    route: "oral",
    controlled: true,
  },
  {
    name: "hydromorphone",
    generic_name: "hydromorphone",
    brand_names: ["Dilaudid"],
    class_id: "opioid_short_acting",
    synonyms: ["dilaudid"],
    route: "oral",
    controlled: true,
  },

  // PAIN - Opioids Long-Acting
  {
    name: "oxycodone ER",
    generic_name: "oxycodone extended-release",
    brand_names: ["OxyContin"],
    class_id: "opioid_long_acting",
    synonyms: ["oxycontin"],
    route: "oral",
    controlled: true,
  },
  {
    name: "morphine ER",
    generic_name: "morphine extended-release",
    brand_names: ["MS Contin"],
    class_id: "opioid_long_acting",
    synonyms: ["ms contin"],
    route: "oral",
    controlled: true,
  },
  {
    name: "fentanyl patch",
    generic_name: "fentanyl transdermal",
    brand_names: ["Duragesic"],
    class_id: "opioid_long_acting",
    synonyms: [],
    route: "patch",
    controlled: true,
  },
  {
    name: "methadone",
    generic_name: "methadone",
    brand_names: ["Dolophine"],
    class_id: "opioid_long_acting",
    synonyms: [],
    route: "oral",
    controlled: true,
  },
  {
    name: "buprenorphine/naloxone",
    generic_name: "buprenorphine/naloxone",
    brand_names: ["Suboxone"],
    class_id: "opioid_long_acting",
    synonyms: ["suboxone"],
    route: "oral",
    controlled: true,
  },
  {
    name: "tapentadol ER",
    generic_name: "tapentadol extended-release",
    brand_names: ["Nucynta ER"],
    class_id: "opioid_long_acting",
    synonyms: ["nucynta"],
    route: "oral",
    controlled: true,
  },
  {
    name: "buprenorphine patch",
    generic_name: "buprenorphine transdermal",
    brand_names: ["Butrans"],
    class_id: "opioid_long_acting",
    synonyms: ["butrans"],
    route: "patch",
    controlled: true,
  },

  // PAIN - Specialty / Adjuncts
  {
    name: "milnacipran",
    generic_name: "milnacipran",
    brand_names: ["Savella"],
    class_id: "pain_adjunct",
    synonyms: [],
    route: "oral",
  },
  {
    name: "triamcinolone ER injection",
    generic_name: "triamcinolone acetonide ER",
    brand_names: ["Zilretta"],
    class_id: "pain_adjunct",
    synonyms: ["zilretta"],
    route: "injection",
  },
  {
    name: "liposomal bupivacaine",
    generic_name: "bupivacaine liposome",
    brand_names: ["Exparel"],
    class_id: "pain_adjunct",
    synonyms: ["exparel"],
    route: "injection",
  },
  {
    name: "naloxegol",
    generic_name: "naloxegol",
    brand_names: ["Movantik"],
    class_id: "oic_agent",
    synonyms: [],
    route: "oral",
  },
  {
    name: "naldemedine",
    generic_name: "naldemedine",
    brand_names: ["Symproic"],
    class_id: "oic_agent",
    synonyms: [],
    route: "oral",
  },
  {
    name: "methylnaltrexone",
    generic_name: "methylnaltrexone",
    brand_names: ["Relistor"],
    class_id: "oic_agent",
    synonyms: [],
    route: "injection",
  },
  {
    name: "low dose naltrexone",
    generic_name: "naltrexone",
    brand_names: ["LDN"],
    class_id: "pain_adjunct",
    synonyms: ["ldn"],
    route: "oral",
  },
  {
    name: "diclofenac topical solution",
    generic_name: "diclofenac sodium topical",
    brand_names: ["Pennsaid"],
    class_id: "topical_analgesic",
    synonyms: ["pennsaid"],
    route: "topical",
  },
  {
    name: "diclofenac patch",
    generic_name: "diclofenac epolamine",
    brand_names: ["Flector"],
    class_id: "topical_analgesic",
    synonyms: ["flector"],
    route: "topical",
  },

  // PSYCHIATRIC - SSRIs
  {
    name: "sertraline",
    generic_name: "sertraline",
    brand_names: ["Zoloft"],
    class_id: "ssri",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "fluoxetine",
    generic_name: "fluoxetine",
    brand_names: ["Prozac"],
    class_id: "ssri",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "escitalopram",
    generic_name: "escitalopram",
    brand_names: ["Lexapro"],
    class_id: "ssri",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "citalopram",
    generic_name: "citalopram",
    brand_names: ["Celexa"],
    class_id: "ssri",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "paroxetine",
    generic_name: "paroxetine",
    brand_names: ["Paxil"],
    class_id: "ssri",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // PSYCHIATRIC - SNRIs
  {
    name: "duloxetine",
    generic_name: "duloxetine",
    brand_names: ["Cymbalta"],
    class_id: "snri",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "venlafaxine",
    generic_name: "venlafaxine",
    brand_names: ["Effexor"],
    class_id: "snri",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "desvenlafaxine",
    generic_name: "desvenlafaxine",
    brand_names: ["Pristiq"],
    class_id: "snri",
    synonyms: [],
    route: "oral",
  },

  // PSYCHIATRIC - TCAs
  {
    name: "amitriptyline",
    generic_name: "amitriptyline",
    brand_names: ["Elavil"],
    class_id: "tca",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "nortriptyline",
    generic_name: "nortriptyline",
    brand_names: ["Pamelor"],
    class_id: "tca",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // PSYCHIATRIC - Other Antidepressants
  {
    name: "bupropion",
    generic_name: "bupropion",
    brand_names: ["Wellbutrin"],
    class_id: "antidepressant_other",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "mirtazapine",
    generic_name: "mirtazapine",
    brand_names: ["Remeron"],
    class_id: "antidepressant_other",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "trazodone",
    generic_name: "trazodone",
    brand_names: ["Desyrel"],
    class_id: "antidepressant_other",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // PSYCHIATRIC - Benzodiazepines
  {
    name: "alprazolam",
    generic_name: "alprazolam",
    brand_names: ["Xanax"],
    class_id: "benzodiazepine",
    synonyms: [],
    route: "oral",
    controlled: true,
    common: true,
  },
  {
    name: "lorazepam",
    generic_name: "lorazepam",
    brand_names: ["Ativan"],
    class_id: "benzodiazepine",
    synonyms: [],
    route: "oral",
    controlled: true,
    common: true,
  },
  {
    name: "clonazepam",
    generic_name: "clonazepam",
    brand_names: ["Klonopin"],
    class_id: "benzodiazepine",
    synonyms: [],
    route: "oral",
    controlled: true,
    common: true,
  },
  {
    name: "diazepam",
    generic_name: "diazepam",
    brand_names: ["Valium"],
    class_id: "benzodiazepine",
    synonyms: [],
    route: "oral",
    controlled: true,
    common: true,
  },

  // PSYCHIATRIC - Antipsychotics
  {
    name: "quetiapine",
    generic_name: "quetiapine",
    brand_names: ["Seroquel"],
    class_id: "antipsychotic_atypical",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "risperidone",
    generic_name: "risperidone",
    brand_names: ["Risperdal"],
    class_id: "antipsychotic_atypical",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "olanzapine",
    generic_name: "olanzapine",
    brand_names: ["Zyprexa"],
    class_id: "antipsychotic_atypical",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "aripiprazole",
    generic_name: "aripiprazole",
    brand_names: ["Abilify"],
    class_id: "antipsychotic_atypical",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "haloperidol",
    generic_name: "haloperidol",
    brand_names: ["Haldol"],
    class_id: "antipsychotic_typical",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // PSYCHIATRIC - Mood Stabilizers
  {
    name: "lithium",
    generic_name: "lithium",
    brand_names: ["Lithobid"],
    class_id: "mood_stabilizer",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "valproate",
    generic_name: "valproic acid",
    brand_names: ["Depakote"],
    class_id: "mood_stabilizer",
    synonyms: ["depakote"],
    route: "oral",
    common: true,
  },
  {
    name: "lamotrigine",
    generic_name: "lamotrigine",
    brand_names: ["Lamictal"],
    class_id: "mood_stabilizer",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // PSYCHIATRIC - Stimulants
  {
    name: "methylphenidate",
    generic_name: "methylphenidate",
    brand_names: ["Ritalin", "Concerta"],
    class_id: "stimulant",
    synonyms: ["ritalin"],
    route: "oral",
    controlled: true,
    common: true,
  },
  {
    name: "amphetamine/dextroamphetamine",
    generic_name: "amphetamine/dextroamphetamine",
    brand_names: ["Adderall"],
    class_id: "stimulant",
    synonyms: ["adderall"],
    route: "oral",
    controlled: true,
    common: true,
  },
  {
    name: "lisdexamfetamine",
    generic_name: "lisdexamfetamine",
    brand_names: ["Vyvanse"],
    class_id: "stimulant",
    synonyms: ["vyvanse"],
    route: "oral",
    controlled: true,
    common: true,
  },

  // PSYCHIATRIC - Non-Stimulant ADHD
  {
    name: "atomoxetine",
    generic_name: "atomoxetine",
    brand_names: ["Strattera"],
    class_id: "non_stimulant_adhd",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // PSYCHIATRIC - Sleep
  {
    name: "zolpidem",
    generic_name: "zolpidem",
    brand_names: ["Ambien"],
    class_id: "hypnotic",
    synonyms: ["ambien"],
    route: "oral",
    controlled: true,
    common: true,
  },
  {
    name: "eszopiclone",
    generic_name: "eszopiclone",
    brand_names: ["Lunesta"],
    class_id: "hypnotic",
    synonyms: [],
    route: "oral",
    controlled: true,
  },

  // CARDIOVASCULAR - ACE Inhibitors
  {
    name: "lisinopril",
    generic_name: "lisinopril",
    brand_names: ["Zestril", "Prinivil"],
    class_id: "ace_inhibitor",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "enalapril",
    generic_name: "enalapril",
    brand_names: ["Vasotec"],
    class_id: "ace_inhibitor",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "ramipril",
    generic_name: "ramipril",
    brand_names: ["Altace"],
    class_id: "ace_inhibitor",
    synonyms: [],
    route: "oral",
  },
  {
    name: "benazepril",
    generic_name: "benazepril",
    brand_names: ["Lotensin"],
    class_id: "ace_inhibitor",
    synonyms: [],
    route: "oral",
  },

  // CARDIOVASCULAR - ARBs
  {
    name: "losartan",
    generic_name: "losartan",
    brand_names: ["Cozaar"],
    class_id: "arb",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "valsartan",
    generic_name: "valsartan",
    brand_names: ["Diovan"],
    class_id: "arb",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "olmesartan",
    generic_name: "olmesartan",
    brand_names: ["Benicar"],
    class_id: "arb",
    synonyms: [],
    route: "oral",
  },
  {
    name: "irbesartan",
    generic_name: "irbesartan",
    brand_names: ["Avapro"],
    class_id: "arb",
    synonyms: [],
    route: "oral",
  },

  // CARDIOVASCULAR - Beta Blockers
  {
    name: "metoprolol succinate",
    generic_name: "metoprolol succinate",
    brand_names: ["Toprol XL"],
    class_id: "beta_blocker",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "metoprolol tartrate",
    generic_name: "metoprolol tartrate",
    brand_names: ["Lopressor"],
    class_id: "beta_blocker",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "carvedilol",
    generic_name: "carvedilol",
    brand_names: ["Coreg"],
    class_id: "beta_blocker",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "atenolol",
    generic_name: "atenolol",
    brand_names: ["Tenormin"],
    class_id: "beta_blocker",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "propranolol",
    generic_name: "propranolol",
    brand_names: ["Inderal"],
    class_id: "beta_blocker",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // CARDIOVASCULAR - CCBs
  {
    name: "amlodipine",
    generic_name: "amlodipine",
    brand_names: ["Norvasc"],
    class_id: "calcium_channel_blocker",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "diltiazem",
    generic_name: "diltiazem",
    brand_names: ["Cardizem"],
    class_id: "calcium_channel_blocker",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "verapamil",
    generic_name: "verapamil",
    brand_names: ["Calan"],
    class_id: "calcium_channel_blocker",
    synonyms: [],
    route: "oral",
  },
  {
    name: "nifedipine",
    generic_name: "nifedipine",
    brand_names: ["Procardia"],
    class_id: "calcium_channel_blocker",
    synonyms: [],
    route: "oral",
  },

  // CARDIOVASCULAR - Diuretics
  {
    name: "hydrochlorothiazide",
    generic_name: "hydrochlorothiazide",
    brand_names: ["Microzide"],
    class_id: "diuretic_thiazide",
    synonyms: ["hctz"],
    route: "oral",
    common: true,
  },
  {
    name: "chlorthalidone",
    generic_name: "chlorthalidone",
    brand_names: ["Thalitone"],
    class_id: "diuretic_thiazide",
    synonyms: [],
    route: "oral",
  },
  {
    name: "furosemide",
    generic_name: "furosemide",
    brand_names: ["Lasix"],
    class_id: "diuretic_loop",
    synonyms: ["lasix"],
    route: "oral",
    common: true,
  },
  {
    name: "bumetanide",
    generic_name: "bumetanide",
    brand_names: ["Bumex"],
    class_id: "diuretic_loop",
    synonyms: [],
    route: "oral",
  },
  {
    name: "spironolactone",
    generic_name: "spironolactone",
    brand_names: ["Aldactone"],
    class_id: "diuretic_potassium_sparing",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // CARDIOVASCULAR - Statins
  {
    name: "atorvastatin",
    generic_name: "atorvastatin",
    brand_names: ["Lipitor"],
    class_id: "statin",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "rosuvastatin",
    generic_name: "rosuvastatin",
    brand_names: ["Crestor"],
    class_id: "statin",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "simvastatin",
    generic_name: "simvastatin",
    brand_names: ["Zocor"],
    class_id: "statin",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "pravastatin",
    generic_name: "pravastatin",
    brand_names: ["Pravachol"],
    class_id: "statin",
    synonyms: [],
    route: "oral",
  },

  // CARDIOVASCULAR - Other Lipid
  {
    name: "ezetimibe",
    generic_name: "ezetimibe",
    brand_names: ["Zetia"],
    class_id: "non_statin_lipid",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "fenofibrate",
    generic_name: "fenofibrate",
    brand_names: ["Tricor"],
    class_id: "non_statin_lipid",
    synonyms: [],
    route: "oral",
  },
  {
    name: "evolocumab",
    generic_name: "evolocumab",
    brand_names: ["Repatha"],
    class_id: "pcsk9_inhibitor",
    synonyms: [],
    route: "injection",
  },
  {
    name: "alirocumab",
    generic_name: "alirocumab",
    brand_names: ["Praluent"],
    class_id: "pcsk9_inhibitor",
    synonyms: [],
    route: "injection",
  },

  // CARDIOVASCULAR - Anticoagulants/Antiplatelets
  {
    name: "warfarin",
    generic_name: "warfarin",
    brand_names: ["Coumadin"],
    class_id: "anticoagulant",
    synonyms: ["coumadin"],
    route: "oral",
    common: true,
  },
  {
    name: "apixaban",
    generic_name: "apixaban",
    brand_names: ["Eliquis"],
    class_id: "doac",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "rivaroxaban",
    generic_name: "rivaroxaban",
    brand_names: ["Xarelto"],
    class_id: "doac",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "dabigatran",
    generic_name: "dabigatran",
    brand_names: ["Pradaxa"],
    class_id: "doac",
    synonyms: [],
    route: "oral",
  },
  {
    name: "clopidogrel",
    generic_name: "clopidogrel",
    brand_names: ["Plavix"],
    class_id: "antiplatelet",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // CARDIOVASCULAR - Other
  {
    name: "amiodarone",
    generic_name: "amiodarone",
    brand_names: ["Cordarone"],
    class_id: "antiarrhythmic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "digoxin",
    generic_name: "digoxin",
    brand_names: ["Lanoxin"],
    class_id: "antiarrhythmic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "nitroglycerin",
    generic_name: "nitroglycerin",
    brand_names: ["Nitrostat"],
    class_id: "nitrate",
    synonyms: ["ntg"],
    route: "oral",
    common: true,
  },
  {
    name: "sacubitril/valsartan",
    generic_name: "sacubitril/valsartan",
    brand_names: ["Entresto"],
    class_id: "arni",
    synonyms: ["entresto"],
    route: "oral",
    common: true,
  },

  // DIABETES
  {
    name: "metformin",
    generic_name: "metformin",
    brand_names: ["Glucophage"],
    class_id: "metformin",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "glipizide",
    generic_name: "glipizide",
    brand_names: ["Glucotrol"],
    class_id: "sulfonylurea",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "glimepiride",
    generic_name: "glimepiride",
    brand_names: ["Amaryl"],
    class_id: "sulfonylurea",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "sitagliptin",
    generic_name: "sitagliptin",
    brand_names: ["Januvia"],
    class_id: "dpp4_inhibitor",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "empagliflozin",
    generic_name: "empagliflozin",
    brand_names: ["Jardiance"],
    class_id: "sglt2_inhibitor",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "dapagliflozin",
    generic_name: "dapagliflozin",
    brand_names: ["Farxiga"],
    class_id: "sglt2_inhibitor",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "semaglutide",
    generic_name: "semaglutide",
    brand_names: ["Ozempic", "Wegovy"],
    class_id: "glp1_agonist",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "liraglutide",
    generic_name: "liraglutide",
    brand_names: ["Victoza"],
    class_id: "glp1_agonist",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "dulaglutide",
    generic_name: "dulaglutide",
    brand_names: ["Trulicity"],
    class_id: "glp1_agonist",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "tirzepatide",
    generic_name: "tirzepatide",
    brand_names: ["Mounjaro", "Zepbound"],
    class_id: "glp1_agonist",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "insulin glargine",
    generic_name: "insulin glargine",
    brand_names: ["Lantus", "Basaglar"],
    class_id: "insulin_basal",
    synonyms: ["lantus"],
    route: "injection",
    common: true,
  },
  {
    name: "insulin lispro",
    generic_name: "insulin lispro",
    brand_names: ["Humalog"],
    class_id: "insulin_bolus",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "insulin aspart",
    generic_name: "insulin aspart",
    brand_names: ["Novolog"],
    class_id: "insulin_bolus",
    synonyms: [],
    route: "injection",
    common: true,
  },

  // RESPIRATORY
  {
    name: "albuterol",
    generic_name: "albuterol",
    brand_names: ["ProAir", "Ventolin"],
    class_id: "inhaled_saba",
    synonyms: [],
    route: "inhaled",
    common: true,
  },
  {
    name: "fluticasone propionate inhaled",
    generic_name: "fluticasone propionate",
    brand_names: ["Flovent"],
    class_id: "inhaled_ics",
    synonyms: [],
    route: "inhaled",
    common: true,
  },
  {
    name: "budesonide inhaled",
    generic_name: "budesonide",
    brand_names: ["Pulmicort"],
    class_id: "inhaled_ics",
    synonyms: [],
    route: "inhaled",
    common: true,
  },
  {
    name: "fluticasone/salmeterol",
    generic_name: "fluticasone/salmeterol",
    brand_names: ["Advair"],
    class_id: "inhaled_ics_laba",
    synonyms: ["advair"],
    route: "inhaled",
    common: true,
  },
  {
    name: "budesonide/formoterol",
    generic_name: "budesonide/formoterol",
    brand_names: ["Symbicort"],
    class_id: "inhaled_ics_laba",
    synonyms: ["symbicort"],
    route: "inhaled",
    common: true,
  },
  {
    name: "fluticasone furoate/vilanterol",
    generic_name: "fluticasone furoate/vilanterol",
    brand_names: ["Breo Ellipta"],
    class_id: "inhaled_ics_laba",
    synonyms: ["breo"],
    route: "inhaled",
    common: true,
  },
  {
    name: "tiotropium",
    generic_name: "tiotropium",
    brand_names: ["Spiriva"],
    class_id: "inhaled_lama",
    synonyms: ["spiriva"],
    route: "inhaled",
    common: true,
  },
  {
    name: "fluticasone/umeclidinium/vilanterol",
    generic_name: "fluticasone furoate/umeclidinium/vilanterol",
    brand_names: ["Trelegy Ellipta"],
    class_id: "inhaled_triple",
    synonyms: ["trelegy"],
    route: "inhaled",
    common: true,
  },
  {
    name: "montelukast",
    generic_name: "montelukast",
    brand_names: ["Singulair"],
    class_id: "leukotriene_modifier",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "dupilumab",
    generic_name: "dupilumab",
    brand_names: ["Dupixent"],
    class_id: "biologic_asthma",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "omalizumab",
    generic_name: "omalizumab",
    brand_names: ["Xolair"],
    class_id: "biologic_asthma",
    synonyms: [],
    route: "injection",
  },
  {
    name: "mepolizumab",
    generic_name: "mepolizumab",
    brand_names: ["Nucala"],
    class_id: "biologic_asthma",
    synonyms: ["nucala"],
    route: "injection",
    common: true,
  },
  {
    name: "benralizumab",
    generic_name: "benralizumab",
    brand_names: ["Fasenra"],
    class_id: "biologic_asthma",
    synonyms: ["fasenra"],
    route: "injection",
    common: true,
  },
  {
    name: "reslizumab",
    generic_name: "reslizumab",
    brand_names: ["Cinqair"],
    class_id: "biologic_asthma",
    synonyms: ["cinqair"],
    route: "iv",
  },
  {
    name: "tezepelumab",
    generic_name: "tezepelumab",
    brand_names: ["Tezspire"],
    class_id: "biologic_asthma",
    synonyms: ["tezspire"],
    route: "injection",
    common: true,
  },

  // RESPIRATORY — CFTR modulators
  {
    name: "elexacaftor/tezacaftor/ivacaftor",
    generic_name: "elexacaftor/tezacaftor/ivacaftor",
    brand_names: ["Trikafta", "Kaftrio"],
    class_id: "cftr_modulator",
    synonyms: ["trikafta", "kaftrio"],
    route: "oral",
    common: true,
  },
  {
    name: "tezacaftor/ivacaftor",
    generic_name: "tezacaftor/ivacaftor",
    brand_names: ["Symdeko", "Symkevi"],
    class_id: "cftr_modulator",
    synonyms: ["symdeko", "symkevi"],
    route: "oral",
  },
  {
    name: "lumacaftor/ivacaftor",
    generic_name: "lumacaftor/ivacaftor",
    brand_names: ["Orkambi"],
    class_id: "cftr_modulator",
    synonyms: ["orkambi"],
    route: "oral",
  },
  {
    name: "ivacaftor",
    generic_name: "ivacaftor",
    brand_names: ["Kalydeco"],
    class_id: "cftr_modulator",
    synonyms: ["kalydeco"],
    route: "oral",
  },

  // RESPIRATORY — Antifibrotics (IPF / PPF)
  {
    name: "pirfenidone",
    generic_name: "pirfenidone",
    brand_names: ["Esbriet"],
    class_id: "antifibrotic_pulmonary",
    synonyms: ["esbriet"],
    route: "oral",
    common: true,
  },
  {
    name: "nintedanib",
    generic_name: "nintedanib",
    brand_names: ["Ofev"],
    class_id: "antifibrotic_pulmonary",
    synonyms: ["ofev"],
    route: "oral",
    common: true,
  },

  // RESPIRATORY — PAH: Endothelin Receptor Antagonists
  {
    name: "ambrisentan",
    generic_name: "ambrisentan",
    brand_names: ["Letairis", "Volibris"],
    class_id: "pah_era",
    synonyms: ["letairis"],
    route: "oral",
  },
  {
    name: "macitentan",
    generic_name: "macitentan",
    brand_names: ["Opsumit"],
    class_id: "pah_era",
    synonyms: ["opsumit"],
    route: "oral",
    common: true,
  },
  {
    name: "bosentan",
    generic_name: "bosentan",
    brand_names: ["Tracleer"],
    class_id: "pah_era",
    synonyms: ["tracleer"],
    route: "oral",
  },

  // RESPIRATORY — PAH: PDE5 inhibitors (PAH-specific brands)
  {
    name: "tadalafil for PAH",
    generic_name: "tadalafil",
    brand_names: ["Adcirca", "Alyq"],
    class_id: "pah_pde5",
    synonyms: ["adcirca", "alyq"],
    route: "oral",
    common: true,
  },
  {
    name: "sildenafil for PAH",
    generic_name: "sildenafil",
    brand_names: ["Revatio"],
    class_id: "pah_pde5",
    synonyms: ["revatio"],
    route: "oral",
    common: true,
  },

  // RESPIRATORY — PAH: sGC stimulators
  {
    name: "riociguat",
    generic_name: "riociguat",
    brand_names: ["Adempas"],
    class_id: "pah_sgc_stimulator",
    synonyms: ["adempas"],
    route: "oral",
    common: true,
  },

  // RESPIRATORY — PAH: Prostacyclin pathway agents
  {
    name: "treprostinil inhaled",
    generic_name: "treprostinil",
    brand_names: ["Tyvaso"],
    class_id: "pah_prostacyclin",
    synonyms: ["tyvaso"],
    route: "inhaled",
    common: true,
  },
  {
    name: "treprostinil oral",
    generic_name: "treprostinil",
    brand_names: ["Orenitram"],
    class_id: "pah_prostacyclin",
    synonyms: ["orenitram"],
    route: "oral",
  },
  {
    name: "treprostinil parenteral",
    generic_name: "treprostinil",
    brand_names: ["Remodulin"],
    class_id: "pah_prostacyclin",
    synonyms: ["remodulin"],
    route: "injection",
  },
  {
    name: "selexipag",
    generic_name: "selexipag",
    brand_names: ["Uptravi"],
    class_id: "pah_prostacyclin",
    synonyms: ["uptravi"],
    route: "oral",
    common: true,
  },
  {
    name: "epoprostenol",
    generic_name: "epoprostenol",
    brand_names: ["Flolan", "Veletri"],
    class_id: "pah_prostacyclin",
    synonyms: ["flolan", "veletri"],
    route: "iv",
  },
  {
    name: "iloprost",
    generic_name: "iloprost",
    brand_names: ["Ventavis"],
    class_id: "pah_prostacyclin",
    synonyms: ["ventavis"],
    route: "inhaled",
  },

  // RESPIRATORY — Alpha-1 antitrypsin augmentation
  {
    name: "alpha-1 proteinase inhibitor (Prolastin-C)",
    generic_name: "alpha-1 proteinase inhibitor",
    brand_names: ["Prolastin-C", "Prolastin"],
    class_id: "alpha1_augmentation",
    synonyms: ["prolastin"],
    route: "iv",
    common: true,
  },
  {
    name: "alpha-1 proteinase inhibitor (Zemaira)",
    generic_name: "alpha-1 proteinase inhibitor",
    brand_names: ["Zemaira"],
    class_id: "alpha1_augmentation",
    synonyms: ["zemaira"],
    route: "iv",
  },
  {
    name: "alpha-1 proteinase inhibitor (Glassia)",
    generic_name: "alpha-1 proteinase inhibitor",
    brand_names: ["Glassia"],
    class_id: "alpha1_augmentation",
    synonyms: ["glassia"],
    route: "iv",
  },
  {
    name: "alpha-1 proteinase inhibitor (Aralast NP)",
    generic_name: "alpha-1 proteinase inhibitor",
    brand_names: ["Aralast NP"],
    class_id: "alpha1_augmentation",
    synonyms: ["aralast"],
    route: "iv",
  },

  // RESPIRATORY — COPD adjuncts
  {
    name: "roflumilast",
    generic_name: "roflumilast",
    brand_names: ["Daliresp", "Daxas"],
    class_id: "pde4_inhibitor",
    synonyms: ["daliresp"],
    route: "oral",
    common: true,
  },
  {
    name: "ensifentrine",
    generic_name: "ensifentrine",
    brand_names: ["Ohtuvayre"],
    class_id: "pde3_pde4_inhibitor",
    synonyms: ["ohtuvayre"],
    route: "inhaled",
    common: true,
  },

  // RESPIRATORY — LABA standalone (filling existing class)
  {
    name: "salmeterol",
    generic_name: "salmeterol",
    brand_names: ["Serevent"],
    class_id: "inhaled_laba",
    synonyms: ["serevent"],
    route: "inhaled",
  },
  {
    name: "formoterol",
    generic_name: "formoterol",
    brand_names: ["Foradil", "Perforomist"],
    class_id: "inhaled_laba",
    synonyms: ["perforomist"],
    route: "inhaled",
  },
  {
    name: "arformoterol",
    generic_name: "arformoterol",
    brand_names: ["Brovana"],
    class_id: "inhaled_laba",
    synonyms: ["brovana"],
    route: "inhaled",
  },
  {
    name: "olodaterol",
    generic_name: "olodaterol",
    brand_names: ["Striverdi Respimat"],
    class_id: "inhaled_laba",
    synonyms: ["striverdi"],
    route: "inhaled",
  },
  {
    name: "indacaterol",
    generic_name: "indacaterol",
    brand_names: ["Arcapta Neohaler"],
    class_id: "inhaled_laba",
    synonyms: ["arcapta"],
    route: "inhaled",
  },

  // RESPIRATORY — additional ICS standalone
  {
    name: "mometasone inhaled",
    generic_name: "mometasone furoate",
    brand_names: ["Asmanex"],
    class_id: "inhaled_ics",
    synonyms: ["asmanex"],
    route: "inhaled",
  },
  {
    name: "ciclesonide inhaled",
    generic_name: "ciclesonide",
    brand_names: ["Alvesco"],
    class_id: "inhaled_ics",
    synonyms: ["alvesco"],
    route: "inhaled",
  },
  {
    name: "beclomethasone inhaled",
    generic_name: "beclomethasone dipropionate",
    brand_names: ["Qvar"],
    class_id: "inhaled_ics",
    synonyms: ["qvar"],
    route: "inhaled",
  },
  {
    name: "fluticasone furoate inhaled",
    generic_name: "fluticasone furoate",
    brand_names: ["Arnuity Ellipta"],
    class_id: "inhaled_ics",
    synonyms: ["arnuity"],
    route: "inhaled",
  },

  // RESPIRATORY — additional ICS/LABA combo
  {
    name: "mometasone/formoterol",
    generic_name: "mometasone/formoterol",
    brand_names: ["Dulera"],
    class_id: "inhaled_ics_laba",
    synonyms: ["dulera"],
    route: "inhaled",
    common: true,
  },

  // RESPIRATORY — additional LAMA standalone
  {
    name: "aclidinium",
    generic_name: "aclidinium",
    brand_names: ["Tudorza Pressair"],
    class_id: "inhaled_lama",
    synonyms: ["tudorza"],
    route: "inhaled",
  },
  {
    name: "umeclidinium",
    generic_name: "umeclidinium",
    brand_names: ["Incruse Ellipta"],
    class_id: "inhaled_lama",
    synonyms: ["incruse"],
    route: "inhaled",
  },
  {
    name: "glycopyrrolate inhaled",
    generic_name: "glycopyrrolate",
    brand_names: ["Seebri Neohaler", "Lonhala Magnair"],
    class_id: "inhaled_lama",
    synonyms: ["seebri", "lonhala"],
    route: "inhaled",
  },
  {
    name: "revefenacin",
    generic_name: "revefenacin",
    brand_names: ["Yupelri"],
    class_id: "inhaled_lama",
    synonyms: ["yupelri"],
    route: "inhaled",
  },

  // RESPIRATORY — LAMA/LABA combinations
  {
    name: "umeclidinium/vilanterol",
    generic_name: "umeclidinium/vilanterol",
    brand_names: ["Anoro Ellipta"],
    class_id: "inhaled_lama_laba",
    synonyms: ["anoro"],
    route: "inhaled",
    common: true,
  },
  {
    name: "glycopyrrolate/formoterol",
    generic_name: "glycopyrrolate/formoterol",
    brand_names: ["Bevespi Aerosphere"],
    class_id: "inhaled_lama_laba",
    synonyms: ["bevespi"],
    route: "inhaled",
  },
  {
    name: "tiotropium/olodaterol",
    generic_name: "tiotropium/olodaterol",
    brand_names: ["Stiolto Respimat"],
    class_id: "inhaled_lama_laba",
    synonyms: ["stiolto"],
    route: "inhaled",
    common: true,
  },
  {
    name: "aclidinium/formoterol",
    generic_name: "aclidinium/formoterol",
    brand_names: ["Duaklir Pressair"],
    class_id: "inhaled_lama_laba",
    synonyms: ["duaklir"],
    route: "inhaled",
  },
  {
    name: "indacaterol/glycopyrrolate",
    generic_name: "indacaterol/glycopyrrolate",
    brand_names: ["Utibron Neohaler"],
    class_id: "inhaled_lama_laba",
    synonyms: ["utibron"],
    route: "inhaled",
  },

  // RESPIRATORY — additional triple therapy
  {
    name: "budesonide/glycopyrrolate/formoterol",
    generic_name: "budesonide/glycopyrrolate/formoterol",
    brand_names: ["Breztri Aerosphere"],
    class_id: "inhaled_triple",
    synonyms: ["breztri"],
    route: "inhaled",
    common: true,
  },

  // RESPIRATORY — additional leukotriene modifiers
  {
    name: "zafirlukast",
    generic_name: "zafirlukast",
    brand_names: ["Accolate"],
    class_id: "leukotriene_modifier",
    synonyms: ["accolate"],
    route: "oral",
  },
  {
    name: "zileuton",
    generic_name: "zileuton",
    brand_names: ["Zyflo"],
    class_id: "leukotriene_modifier",
    synonyms: ["zyflo"],
    route: "oral",
  },

  // RESPIRATORY — PAH activin signaling inhibitor (newer mechanism)
  {
    name: "sotatercept",
    generic_name: "sotatercept",
    brand_names: ["Winrevair"],
    class_id: "pah_activin_inhibitor",
    synonyms: ["winrevair"],
    route: "injection",
    common: true,
  },

  // RESPIRATORY — CF mucoactive agents
  {
    name: "dornase alfa",
    generic_name: "dornase alfa",
    brand_names: ["Pulmozyme"],
    class_id: "cf_mucoactive",
    synonyms: ["pulmozyme"],
    route: "inhaled",
    common: true,
  },
  {
    name: "hypertonic saline inhaled",
    generic_name: "hypertonic saline 7%",
    brand_names: ["HyperSal", "PulmoSal"],
    class_id: "cf_mucoactive",
    synonyms: ["hypertonic saline"],
    route: "inhaled",
  },
  {
    name: "mannitol inhaled",
    generic_name: "mannitol",
    brand_names: ["Bronchitol"],
    class_id: "cf_mucoactive",
    synonyms: ["bronchitol"],
    route: "inhaled",
  },

  // RESPIRATORY — Inhaled antibiotics (CF / bronchiectasis / NTM)
  {
    name: "tobramycin inhaled",
    generic_name: "tobramycin",
    brand_names: ["TOBI", "TOBI Podhaler", "Bethkis", "Kitabis Pak"],
    class_id: "inhaled_antibiotic",
    synonyms: ["tobi", "bethkis"],
    route: "inhaled",
    common: true,
  },
  {
    name: "aztreonam inhaled",
    generic_name: "aztreonam",
    brand_names: ["Cayston"],
    class_id: "inhaled_antibiotic",
    synonyms: ["cayston"],
    route: "inhaled",
    common: true,
  },
  {
    name: "amikacin liposomal inhalation",
    generic_name: "amikacin liposomal",
    brand_names: ["Arikayce"],
    class_id: "inhaled_antibiotic",
    synonyms: ["arikayce"],
    route: "inhaled",
    common: true,
  },
  {
    name: "colistimethate inhaled",
    generic_name: "colistimethate sodium",
    brand_names: ["Coly-Mycin M"],
    class_id: "inhaled_antibiotic",
    synonyms: ["colistin inhaled"],
    route: "inhaled",
  },

  // RESPIRATORY — ACTH analog (sarcoidosis and other steroid-refractory)
  {
    name: "repository corticotropin",
    generic_name: "repository corticotropin",
    brand_names: ["Acthar Gel", "H.P. Acthar Gel"],
    class_id: "acth_analog",
    synonyms: ["acthar", "acthar gel"],
    route: "injection",
  },
  {
    name: "corticotropin lyophilized",
    generic_name: "corticotropin lyophilized",
    brand_names: ["Purified Cortrophin Gel"],
    class_id: "acth_analog",
    synonyms: ["cortrophin"],
    route: "injection",
  },

  // RESPIRATORY — chronic cough
  {
    name: "gefapixant",
    generic_name: "gefapixant",
    brand_names: ["Lyfnua"],
    class_id: "chronic_cough_p2x3",
    synonyms: ["lyfnua"],
    route: "oral",
  },

  // RESPIRATORY — Smoking cessation
  {
    name: "varenicline",
    generic_name: "varenicline",
    brand_names: ["Chantix"],
    class_id: "smoking_cessation",
    synonyms: ["chantix"],
    route: "oral",
    common: true,
  },
  {
    name: "bupropion for smoking cessation",
    generic_name: "bupropion SR",
    brand_names: ["Zyban"],
    class_id: "smoking_cessation",
    synonyms: ["zyban"],
    route: "oral",
    common: true,
  },
  {
    name: "nicotine replacement patch",
    generic_name: "nicotine transdermal",
    brand_names: ["NicoDerm CQ", "Habitrol"],
    class_id: "smoking_cessation",
    synonyms: ["nicotine patch"],
    route: "patch",
    common: true,
  },
  {
    name: "nicotine gum",
    generic_name: "nicotine polacrilex gum",
    brand_names: ["Nicorette"],
    class_id: "smoking_cessation",
    synonyms: ["nicorette"],
    route: "oral",
  },
  {
    name: "nicotine lozenge",
    generic_name: "nicotine lozenge",
    brand_names: ["Nicorette Lozenge", "Commit"],
    class_id: "smoking_cessation",
    synonyms: [],
    route: "oral",
  },
  {
    name: "nicotine nasal spray",
    generic_name: "nicotine nasal spray",
    brand_names: ["Nicotrol NS"],
    class_id: "smoking_cessation",
    synonyms: ["nicotrol"],
    route: "nasal",
  },
  {
    name: "nicotine inhaler",
    generic_name: "nicotine oral inhaler",
    brand_names: ["Nicotrol Inhaler"],
    class_id: "smoking_cessation",
    synonyms: [],
    route: "inhaled",
  },

  // RHEUMATOLOGY/IMMUNOLOGY
  {
    name: "prednisone",
    generic_name: "prednisone",
    brand_names: ["Deltasone"],
    class_id: "corticosteroid_oral",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "methylprednisolone",
    generic_name: "methylprednisolone",
    brand_names: ["Medrol"],
    class_id: "corticosteroid_oral",
    synonyms: ["medrol dose pack"],
    route: "oral",
    common: true,
  },
  {
    name: "dexamethasone",
    generic_name: "dexamethasone",
    brand_names: ["Decadron"],
    class_id: "corticosteroid_oral",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "triamcinolone injection",
    generic_name: "triamcinolone acetonide",
    brand_names: ["Kenalog"],
    class_id: "corticosteroid_injection",
    synonyms: ["kenalog"],
    route: "injection",
    common: true,
  },
  {
    name: "methotrexate",
    generic_name: "methotrexate",
    brand_names: ["Rheumatrex"],
    class_id: "dmard_conventional",
    synonyms: ["mtx"],
    route: "oral",
    common: true,
  },
  {
    name: "hydroxychloroquine",
    generic_name: "hydroxychloroquine",
    brand_names: ["Plaquenil"],
    class_id: "dmard_conventional",
    synonyms: ["plaquenil"],
    route: "oral",
    common: true,
  },
  {
    name: "sulfasalazine",
    generic_name: "sulfasalazine",
    brand_names: ["Azulfidine"],
    class_id: "dmard_conventional",
    synonyms: [],
    route: "oral",
  },
  {
    name: "leflunomide",
    generic_name: "leflunomide",
    brand_names: ["Arava"],
    class_id: "dmard_conventional",
    synonyms: [],
    route: "oral",
  },
  {
    name: "adalimumab",
    generic_name: "adalimumab",
    brand_names: ["Humira"],
    class_id: "biologic_tnf",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "etanercept",
    generic_name: "etanercept",
    brand_names: ["Enbrel"],
    class_id: "biologic_tnf",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "infliximab",
    generic_name: "infliximab",
    brand_names: ["Remicade"],
    class_id: "biologic_tnf",
    synonyms: [],
    route: "iv",
    common: true,
  },
  {
    name: "secukinumab",
    generic_name: "secukinumab",
    brand_names: ["Cosentyx"],
    class_id: "biologic_il17",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "ustekinumab",
    generic_name: "ustekinumab",
    brand_names: ["Stelara"],
    class_id: "biologic_il12_23",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "tofacitinib",
    generic_name: "tofacitinib",
    brand_names: ["Xeljanz"],
    class_id: "jak_inhibitor",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "rituximab",
    generic_name: "rituximab",
    brand_names: ["Rituxan"],
    class_id: "biologic_b_cell",
    synonyms: [],
    route: "iv",
    common: true,
  },
  {
    name: "colchicine",
    generic_name: "colchicine",
    brand_names: ["Colcrys"],
    class_id: "colchicine",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "allopurinol",
    generic_name: "allopurinol",
    brand_names: ["Zyloprim"],
    class_id: "urate_lowering",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // GI
  {
    name: "omeprazole",
    generic_name: "omeprazole",
    brand_names: ["Prilosec"],
    class_id: "ppi",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "esomeprazole",
    generic_name: "esomeprazole",
    brand_names: ["Nexium"],
    class_id: "ppi",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "pantoprazole",
    generic_name: "pantoprazole",
    brand_names: ["Protonix"],
    class_id: "ppi",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "famotidine",
    generic_name: "famotidine",
    brand_names: ["Pepcid"],
    class_id: "h2_blocker",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "metoclopramide",
    generic_name: "metoclopramide",
    brand_names: ["Reglan"],
    class_id: "prokinetic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "dicyclomine",
    generic_name: "dicyclomine",
    brand_names: ["Bentyl"],
    class_id: "antispasmodic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "polyethylene glycol",
    generic_name: "polyethylene glycol 3350",
    brand_names: ["Miralax"],
    class_id: "laxative",
    synonyms: ["peg", "miralax"],
    route: "oral",
    common: true,
  },
  {
    name: "lactulose",
    generic_name: "lactulose",
    brand_names: ["Kristalose"],
    class_id: "laxative",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "loperamide",
    generic_name: "loperamide",
    brand_names: ["Imodium"],
    class_id: "antidiarrheal",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "mesalamine",
    generic_name: "mesalamine",
    brand_names: ["Asacol", "Lialda"],
    class_id: "aminosalicylate",
    synonyms: ["5-asa"],
    route: "oral",
    common: true,
  },
  {
    name: "ondansetron",
    generic_name: "ondansetron",
    brand_names: ["Zofran"],
    class_id: "antiemetic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "promethazine",
    generic_name: "promethazine",
    brand_names: ["Phenergan"],
    class_id: "antiemetic",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // NEUROLOGY
  {
    name: "levetiracetam",
    generic_name: "levetiracetam",
    brand_names: ["Keppra"],
    class_id: "anticonvulsant",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "topiramate",
    generic_name: "topiramate",
    brand_names: ["Topamax"],
    class_id: "anticonvulsant",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "phenytoin",
    generic_name: "phenytoin",
    brand_names: ["Dilantin"],
    class_id: "anticonvulsant",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "carbamazepine",
    generic_name: "carbamazepine",
    brand_names: ["Tegretol"],
    class_id: "anticonvulsant",
    synonyms: [],
    route: "oral",
  },
  {
    name: "sumatriptan",
    generic_name: "sumatriptan",
    brand_names: ["Imitrex"],
    class_id: "triptan",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "rizatriptan",
    generic_name: "rizatriptan",
    brand_names: ["Maxalt"],
    class_id: "triptan",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "erenumab",
    generic_name: "erenumab",
    brand_names: ["Aimovig"],
    class_id: "cgrp_inhibitor",
    synonyms: [],
    route: "injection",
    common: true,
  },
  {
    name: "carbidopa/levodopa",
    generic_name: "carbidopa/levodopa",
    brand_names: ["Sinemet"],
    class_id: "parkinsons_dopamine",
    synonyms: ["sinemet"],
    route: "oral",
    common: true,
  },
  {
    name: "pramipexole",
    generic_name: "pramipexole",
    brand_names: ["Mirapex"],
    class_id: "parkinsons_dopamine",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "donepezil",
    generic_name: "donepezil",
    brand_names: ["Aricept"],
    class_id: "alzheimers",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "memantine",
    generic_name: "memantine",
    brand_names: ["Namenda"],
    class_id: "alzheimers",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // ALLERGY
  {
    name: "cetirizine",
    generic_name: "cetirizine",
    brand_names: ["Zyrtec"],
    class_id: "antihistamine",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "loratadine",
    generic_name: "loratadine",
    brand_names: ["Claritin"],
    class_id: "antihistamine",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "fexofenadine",
    generic_name: "fexofenadine",
    brand_names: ["Allegra"],
    class_id: "antihistamine",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "diphenhydramine",
    generic_name: "diphenhydramine",
    brand_names: ["Benadryl"],
    class_id: "antihistamine",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "hydroxyzine",
    generic_name: "hydroxyzine",
    brand_names: ["Vistaril"],
    class_id: "antihistamine",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "fluticasone nasal",
    generic_name: "fluticasone propionate nasal",
    brand_names: ["Flonase"],
    class_id: "nasal_steroid",
    synonyms: [],
    route: "nasal",
    common: true,
  },
  {
    name: "epinephrine auto-injector",
    generic_name: "epinephrine",
    brand_names: ["EpiPen"],
    class_id: "epinephrine",
    synonyms: ["epipen"],
    route: "injection",
    common: true,
  },

  // DERMATOLOGY
  {
    name: "triamcinolone topical",
    generic_name: "triamcinolone acetonide topical",
    brand_names: ["Kenalog cream"],
    class_id: "topical_steroid",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "clobetasol",
    generic_name: "clobetasol propionate",
    brand_names: ["Temovate"],
    class_id: "topical_steroid",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "betamethasone dipropionate",
    generic_name: "betamethasone dipropionate",
    brand_names: ["Diprolene"],
    class_id: "topical_steroid",
    synonyms: [],
    route: "topical",
  },
  {
    name: "fluocinonide",
    generic_name: "fluocinonide",
    brand_names: ["Lidex"],
    class_id: "topical_steroid",
    synonyms: [],
    route: "topical",
  },
  {
    name: "mometasone topical",
    generic_name: "mometasone furoate topical",
    brand_names: ["Elocon"],
    class_id: "topical_steroid",
    synonyms: [],
    route: "topical",
  },
  {
    name: "hydrocortisone topical",
    generic_name: "hydrocortisone topical",
    brand_names: ["Cortaid"],
    class_id: "topical_steroid",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "desonide",
    generic_name: "desonide",
    brand_names: ["Desonate", "DesOwen"],
    class_id: "topical_steroid",
    synonyms: [],
    route: "topical",
  },
  {
    name: "tacrolimus topical",
    generic_name: "tacrolimus topical",
    brand_names: ["Protopic"],
    class_id: "topical_calcineurin",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "pimecrolimus",
    generic_name: "pimecrolimus",
    brand_names: ["Elidel"],
    class_id: "topical_calcineurin",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "tretinoin topical",
    generic_name: "tretinoin",
    brand_names: ["Retin-A", "Retin-A Micro", "Altreno"],
    class_id: "topical_retinoid",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "adapalene",
    generic_name: "adapalene",
    brand_names: ["Differin"],
    class_id: "topical_retinoid",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "tazarotene",
    generic_name: "tazarotene",
    brand_names: ["Tazorac", "Arazlo"],
    class_id: "topical_retinoid",
    synonyms: [],
    route: "topical",
  },
  {
    name: "trifarotene",
    generic_name: "trifarotene",
    brand_names: ["Aklief"],
    class_id: "topical_retinoid",
    synonyms: [],
    route: "topical",
  },
  {
    name: "isotretinoin",
    generic_name: "isotretinoin",
    brand_names: ["Accutane", "Claravis", "Absorica", "Zenatane"],
    class_id: "oral_retinoid",
    synonyms: ["accutane"],
    route: "oral",
  },
  {
    name: "acitretin",
    generic_name: "acitretin",
    brand_names: ["Soriatane"],
    class_id: "oral_retinoid",
    synonyms: [],
    route: "oral",
  },
  {
    name: "terbinafine oral",
    generic_name: "terbinafine",
    brand_names: ["Lamisil"],
    class_id: "antifungal_oral",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "fluconazole",
    generic_name: "fluconazole",
    brand_names: ["Diflucan"],
    class_id: "antifungal_oral",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "clotrimazole topical",
    generic_name: "clotrimazole topical",
    brand_names: ["Lotrimin"],
    class_id: "antifungal_topical",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "ketoconazole topical",
    generic_name: "ketoconazole topical",
    brand_names: ["Nizoral cream"],
    class_id: "antifungal_topical",
    synonyms: [],
    route: "topical",
  },
  {
    name: "nystatin topical",
    generic_name: "nystatin topical",
    brand_names: ["Mycostatin"],
    class_id: "antifungal_topical",
    synonyms: [],
    route: "topical",
  },
  {
    name: "metronidazole topical",
    generic_name: "metronidazole topical",
    brand_names: ["MetroGel", "MetroCream"],
    class_id: "topical_rosacea",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "ivermectin topical",
    generic_name: "ivermectin cream",
    brand_names: ["Soolantra"],
    class_id: "topical_rosacea",
    synonyms: [],
    route: "topical",
  },
  {
    name: "azelaic acid",
    generic_name: "azelaic acid",
    brand_names: ["Finacea", "Azelex"],
    class_id: "topical_rosacea",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "oxymetazoline topical",
    generic_name: "oxymetazoline cream",
    brand_names: ["Rhofade"],
    class_id: "topical_rosacea",
    synonyms: [],
    route: "topical",
  },
  {
    name: "benzoyl peroxide",
    generic_name: "benzoyl peroxide",
    brand_names: ["PanOxyl", "Benzac"],
    class_id: "topical_acne",
    synonyms: ["bpo"],
    route: "topical",
    common: true,
  },
  {
    name: "clindamycin topical",
    generic_name: "clindamycin phosphate topical",
    brand_names: ["Cleocin T", "Clindagel"],
    class_id: "topical_acne",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "dapsone topical",
    generic_name: "dapsone gel",
    brand_names: ["Aczone"],
    class_id: "topical_acne",
    synonyms: [],
    route: "topical",
  },
  {
    name: "clascoterone",
    generic_name: "clascoterone",
    brand_names: ["Winlevi"],
    class_id: "topical_acne",
    synonyms: [],
    route: "topical",
  },
  {
    name: "spironolactone",
    generic_name: "spironolactone",
    brand_names: ["Aldactone"],
    class_id: "hormonal_acne",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "crisaborole",
    generic_name: "crisaborole",
    brand_names: ["Eucrisa"],
    class_id: "topical_calcineurin",
    synonyms: [],
    route: "topical",
  },
  {
    name: "tapinarof",
    generic_name: "tapinarof",
    brand_names: ["Vtama"],
    class_id: "biologic_dermatology",
    synonyms: [],
    route: "topical",
  },
  {
    name: "ruxolitinib topical",
    generic_name: "ruxolitinib cream",
    brand_names: ["Opzelura"],
    class_id: "biologic_dermatology",
    synonyms: [],
    route: "topical",
  },
  {
    name: "imiquimod",
    generic_name: "imiquimod",
    brand_names: ["Aldara", "Zyclara"],
    class_id: "topical_immune_modifier",
    synonyms: [],
    route: "topical",
  },
  {
    name: "5-fluorouracil topical",
    generic_name: "fluorouracil cream",
    brand_names: ["Efudex", "Carac"],
    class_id: "topical_immune_modifier",
    synonyms: ["5-fu cream"],
    route: "topical",
  },
  {
    name: "diclofenac topical",
    generic_name: "diclofenac sodium gel",
    brand_names: ["Solaraze", "Voltaren Gel"],
    class_id: "topical_analgesic",
    synonyms: [],
    route: "topical",
  },
  {
    name: "glycopyrronium topical",
    generic_name: "glycopyrronium cloth",
    brand_names: ["Qbrexza"],
    class_id: "topical_hyperhidrosis",
    synonyms: [],
    route: "topical",
  },
  {
    name: "ciclopirox",
    generic_name: "ciclopirox",
    brand_names: ["Loprox", "Penlac"],
    class_id: "antifungal_topical",
    synonyms: [],
    route: "topical",
  },
  {
    name: "selenium sulfide",
    generic_name: "selenium sulfide",
    brand_names: ["Selsun Blue"],
    class_id: "antifungal_topical",
    synonyms: [],
    route: "topical",
  },
  {
    name: "ketoconazole shampoo",
    generic_name: "ketoconazole shampoo",
    brand_names: ["Nizoral shampoo"],
    class_id: "antifungal_topical",
    synonyms: [],
    route: "topical",
  },
  {
    name: "apremilast",
    generic_name: "apremilast",
    brand_names: ["Otezla"],
    class_id: "biologic_dermatology",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "mycophenolate",
    generic_name: "mycophenolate mofetil",
    brand_names: ["CellCept"],
    class_id: "immunosuppressant_derm",
    synonyms: [],
    route: "oral",
  },
  {
    name: "hydroxychloroquine",
    generic_name: "hydroxychloroquine",
    brand_names: ["Plaquenil"],
    class_id: "immunosuppressant_derm",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "doxycycline",
    generic_name: "doxycycline",
    brand_names: ["Vibramycin", "Oracea"],
    class_id: "topical_acne",
    synonyms: ["oracea"],
    route: "oral",
    common: true,
  },
  {
    name: "minocycline",
    generic_name: "minocycline",
    brand_names: ["Solodyn", "Amzeeq"],
    class_id: "topical_acne",
    synonyms: [],
    route: "oral",
  },

  // UROLOGY
  {
    name: "tamsulosin",
    generic_name: "tamsulosin",
    brand_names: ["Flomax"],
    class_id: "alpha_blocker_uro",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "finasteride",
    generic_name: "finasteride",
    brand_names: ["Proscar"],
    class_id: "five_alpha_reductase",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "oxybutynin",
    generic_name: "oxybutynin",
    brand_names: ["Ditropan"],
    class_id: "overactive_bladder",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "mirabegron",
    generic_name: "mirabegron",
    brand_names: ["Myrbetriq"],
    class_id: "overactive_bladder",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "sildenafil",
    generic_name: "sildenafil",
    brand_names: ["Viagra"],
    class_id: "pde5_inhibitor",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "tadalafil",
    generic_name: "tadalafil",
    brand_names: ["Cialis"],
    class_id: "pde5_inhibitor",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // ENDOCRINE
  {
    name: "levothyroxine",
    generic_name: "levothyroxine",
    brand_names: ["Synthroid"],
    class_id: "thyroid_replacement",
    synonyms: ["t4"],
    route: "oral",
    common: true,
  },
  {
    name: "methimazole",
    generic_name: "methimazole",
    brand_names: ["Tapazole"],
    class_id: "antithyroid",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "alendronate",
    generic_name: "alendronate",
    brand_names: ["Fosamax"],
    class_id: "bisphosphonate",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "testosterone gel",
    generic_name: "testosterone topical",
    brand_names: ["AndroGel"],
    class_id: "testosterone",
    synonyms: [],
    route: "topical",
    common: true,
  },
  {
    name: "estradiol oral",
    generic_name: "estradiol",
    brand_names: ["Estrace"],
    class_id: "estrogen",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // INFECTIOUS
  {
    name: "amoxicillin",
    generic_name: "amoxicillin",
    brand_names: ["Amoxil"],
    class_id: "antibiotic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "amoxicillin/clavulanate",
    generic_name: "amoxicillin/clavulanate",
    brand_names: ["Augmentin"],
    class_id: "antibiotic",
    synonyms: ["augmentin"],
    route: "oral",
    common: true,
  },
  {
    name: "azithromycin",
    generic_name: "azithromycin",
    brand_names: ["Zithromax", "Z-pak"],
    class_id: "antibiotic",
    synonyms: ["z-pak"],
    route: "oral",
    common: true,
  },
  {
    name: "doxycycline",
    generic_name: "doxycycline",
    brand_names: ["Vibramycin"],
    class_id: "antibiotic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "ciprofloxacin",
    generic_name: "ciprofloxacin",
    brand_names: ["Cipro"],
    class_id: "antibiotic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "levofloxacin",
    generic_name: "levofloxacin",
    brand_names: ["Levaquin"],
    class_id: "antibiotic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "cephalexin",
    generic_name: "cephalexin",
    brand_names: ["Keflex"],
    class_id: "antibiotic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "sulfamethoxazole/trimethoprim",
    generic_name: "sulfamethoxazole/trimethoprim",
    brand_names: ["Bactrim"],
    class_id: "antibiotic",
    synonyms: ["bactrim", "tmp-smx"],
    route: "oral",
    common: true,
  },
  {
    name: "metronidazole",
    generic_name: "metronidazole",
    brand_names: ["Flagyl"],
    class_id: "antibiotic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "nitrofurantoin",
    generic_name: "nitrofurantoin",
    brand_names: ["Macrobid"],
    class_id: "antibiotic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "clindamycin",
    generic_name: "clindamycin",
    brand_names: ["Cleocin"],
    class_id: "antibiotic",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "acyclovir",
    generic_name: "acyclovir",
    brand_names: ["Zovirax"],
    class_id: "antiviral",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "valacyclovir",
    generic_name: "valacyclovir",
    brand_names: ["Valtrex"],
    class_id: "antiviral",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "oseltamivir",
    generic_name: "oseltamivir",
    brand_names: ["Tamiflu"],
    class_id: "antiviral",
    synonyms: [],
    route: "oral",
    common: true,
  },

  // WEIGHT MANAGEMENT
  {
    name: "phentermine",
    generic_name: "phentermine",
    brand_names: ["Adipex-P", "Lomaira"],
    class_id: "weight_management",
    synonyms: ["adipex"],
    route: "oral",
    controlled: true,
  },
  {
    name: "naltrexone/bupropion",
    generic_name: "naltrexone/bupropion",
    brand_names: ["Contrave"],
    class_id: "weight_management",
    synonyms: ["contrave"],
    route: "oral",
  },
  {
    name: "phentermine/topiramate",
    generic_name: "phentermine/topiramate ER",
    brand_names: ["Qsymia"],
    class_id: "weight_management",
    synonyms: ["qsymia"],
    route: "oral",
    controlled: true,
  },
  {
    name: "orlistat",
    generic_name: "orlistat",
    brand_names: ["Xenical", "Alli"],
    class_id: "weight_management",
    synonyms: ["alli"],
    route: "oral",
  },

  // HORMONE REPLACEMENT — MENOPAUSE
  {
    name: "estradiol patch",
    generic_name: "estradiol transdermal",
    brand_names: ["Vivelle-Dot", "Climara"],
    class_id: "hrt_menopause",
    synonyms: ["estrogen patch"],
    route: "patch",
    common: true,
  },
  {
    name: "vaginal estrogen",
    generic_name: "estradiol vaginal",
    brand_names: ["Premarin cream", "Estrace cream", "Vagifem", "Imvexxy"],
    class_id: "hrt_menopause",
    synonyms: [],
    route: "topical",
  },
  {
    name: "fezolinetant",
    generic_name: "fezolinetant",
    brand_names: ["Veozah"],
    class_id: "hrt_menopause",
    synonyms: [],
    route: "oral",
  },
  {
    name: "ospemifene",
    generic_name: "ospemifene",
    brand_names: ["Osphena"],
    class_id: "hrt_menopause",
    synonyms: [],
    route: "oral",
  },
  {
    name: "progesterone",
    generic_name: "progesterone micronized",
    brand_names: ["Prometrium"],
    class_id: "hrt_menopause",
    synonyms: [],
    route: "oral",
    common: true,
  },
  {
    name: "desiccated thyroid",
    generic_name: "thyroid extract",
    brand_names: ["Armour Thyroid", "NP Thyroid"],
    class_id: "thyroid_replacement",
    synonyms: ["armour"],
    route: "oral",
  },

  // SUPPLEMENTS
  {
    name: "vitamin D",
    generic_name: "cholecalciferol",
    brand_names: ["Drisdol"],
    class_id: "vitamin_supplement",
    synonyms: ["vitamin d3"],
    route: "oral",
    common: true,
  },
  {
    name: "vitamin B12",
    generic_name: "cyanocobalamin",
    brand_names: ["Nascobal"],
    class_id: "vitamin_supplement",
    synonyms: ["b12"],
    route: "oral",
    common: true,
  },
  {
    name: "folic acid",
    generic_name: "folic acid",
    brand_names: ["Folvite"],
    class_id: "vitamin_supplement",
    synonyms: ["folate"],
    route: "oral",
    common: true,
  },
  {
    name: "iron supplement",
    generic_name: "ferrous sulfate",
    brand_names: ["Feosol"],
    class_id: "vitamin_supplement",
    synonyms: ["iron"],
    route: "oral",
    common: true,
  },
  {
    name: "potassium supplement",
    generic_name: "potassium chloride",
    brand_names: ["Klor-Con"],
    class_id: "vitamin_supplement",
    synonyms: ["kcl"],
    route: "oral",
    common: true,
  },
];

// ============================================
// UTILITY FUNCTIONS
// ============================================

// Build search index for fast lookups
const medicationSearchIndex: Map<string, Medication[]> = new Map();
const medicationByName: Map<string, Medication> = new Map();

function initializeSearchIndex(): void {
  MEDICATIONS.forEach((med) => {
    // Index by generic name
    const genericLower = med.generic_name.toLowerCase();
    addToIndex(genericLower, med);
    medicationByName.set(genericLower, med);

    // Index by name (if different)
    const nameLower = med.name.toLowerCase();
    if (nameLower !== genericLower) {
      addToIndex(nameLower, med);
      medicationByName.set(nameLower, med);
    }

    // Index by brand names
    med.brand_names.forEach((brand) => {
      const brandLower = brand.toLowerCase();
      addToIndex(brandLower, med);
      medicationByName.set(brandLower, med);
    });

    // Index by synonyms
    med.synonyms.forEach((syn) => {
      const synLower = syn.toLowerCase();
      addToIndex(synLower, med);
      medicationByName.set(synLower, med);
    });
  });
}

function addToIndex(key: string, med: Medication): void {
  const existing = medicationSearchIndex.get(key) || [];
  existing.push(med);
  medicationSearchIndex.set(key, existing);
}

// Initialize on module load
initializeSearchIndex();

/**
 * Search for medications by name, brand, or synonym
 * Returns up to `limit` results with match quality info
 */
export function searchMedications(
  query: string,
  limit: number = 10
): MedicationMatch[] {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const queryLower = query.toLowerCase().trim();
  const results: MedicationMatch[] = [];
  const seen = new Set<string>();

  // Exact match first
  const exactMatch = medicationByName.get(queryLower);
  if (exactMatch && !seen.has(exactMatch.name)) {
    seen.add(exactMatch.name);
    results.push({
      medication: exactMatch,
      medicationClass: MEDICATION_CLASSES[exactMatch.class_id],
      matchType: "exact",
      matchedOn: queryLower,
    });
  }

  // Prefix matches (starts with query)
  MEDICATIONS.forEach((med) => {
    if (seen.has(med.name) || results.length >= limit * 2) return;

    // Check generic name
    if (med.generic_name.toLowerCase().startsWith(queryLower)) {
      seen.add(med.name);
      results.push({
        medication: med,
        medicationClass: MEDICATION_CLASSES[med.class_id],
        matchType: "generic",
        matchedOn: med.generic_name,
      });
      return;
    }

    // Check brand names
    for (const brand of med.brand_names) {
      if (brand.toLowerCase().startsWith(queryLower)) {
        seen.add(med.name);
        results.push({
          medication: med,
          medicationClass: MEDICATION_CLASSES[med.class_id],
          matchType: "brand",
          matchedOn: brand,
        });
        return;
      }
    }

    // Check synonyms
    for (const syn of med.synonyms) {
      if (syn.toLowerCase().startsWith(queryLower)) {
        seen.add(med.name);
        results.push({
          medication: med,
          medicationClass: MEDICATION_CLASSES[med.class_id],
          matchType: "synonym",
          matchedOn: syn,
        });
        return;
      }
    }
  });

  // Fuzzy matches (contains query) - only if we need more results
  if (results.length < limit) {
    MEDICATIONS.forEach((med) => {
      if (seen.has(med.name) || results.length >= limit * 2) return;

      const allNames = [
        med.generic_name,
        med.name,
        ...med.brand_names,
        ...med.synonyms,
      ];

      for (const name of allNames) {
        if (name.toLowerCase().includes(queryLower)) {
          seen.add(med.name);
          results.push({
            medication: med,
            medicationClass: MEDICATION_CLASSES[med.class_id],
            matchType: "fuzzy",
            matchedOn: name,
          });
          break;
        }
      }
    });
  }

  // Sort: prioritize common meds, then by match quality
  const matchTypeOrder = {
    exact: 0,
    generic: 1,
    brand: 2,
    synonym: 3,
    fuzzy: 4,
  };
  results.sort((a, b) => {
    // Common meds first
    const aCommon = a.medication.common ? 0 : 1;
    const bCommon = b.medication.common ? 0 : 1;
    if (aCommon !== bCommon) return aCommon - bCommon;

    // Then by match type
    return matchTypeOrder[a.matchType] - matchTypeOrder[b.matchType];
  });

  return results.slice(0, limit);
}

/**
 * Look up a medication by exact name (generic, brand, or synonym)
 */
export function getMedication(name: string): Medication | null {
  if (!name) return null;
  return medicationByName.get(name.toLowerCase().trim()) || null;
}

/**
 * Get the class for a medication
 */
export function getMedicationClass(medication: Medication): MedicationClass {
  return MEDICATION_CLASSES[medication.class_id];
}

/**
 * Get class by ID
 */
export function getClassById(classId: string): MedicationClass | null {
  return MEDICATION_CLASSES[classId] || null;
}

/**
 * Attempt to infer class from medication name (for unknown medications)
 */
export function inferMedicationClass(medName: string): MedicationClass | null {
  const nameLower = medName.toLowerCase();

  // Check for common suffixes/patterns
  const patterns: [RegExp, string][] = [
    // Antibiotics
    [/cillin$/, "antibiotic"],
    [/mycin$/, "antibiotic"],
    [/floxacin$/, "antibiotic"],
    [/cycline$/, "antibiotic"],

    // Blood pressure
    [/pril$/, "ace_inhibitor"],
    [/sartan$/, "arb"],
    [/olol$/, "beta_blocker"],
    [/dipine$/, "calcium_channel_blocker"],

    // Statins
    [/statin$/, "statin"],

    // PPIs
    [/prazole$/, "ppi"],

    // Diabetes
    [/gliptin$/, "dpp4_inhibitor"],
    [/gliflozin$/, "sglt2_inhibitor"],
    [/glutide$/, "glp1_agonist"],

    // Psychiatric
    [/pram$/, "ssri"],
    [/xetine$/, "ssri"],
    [/pine$/, "antipsychotic_atypical"],
    [/done$/, "antipsychotic_atypical"],

    // Pain
    [/profen$/, "nsaid"],
    [/coxib$/, "nsaid"],
    [/codone$/, "opioid_short_acting"],
    [/morphine$/, "opioid"],

    // Biologics
    [/mab$/, "biologic_tnf"], // Many monoclonal antibodies
    [/cept$/, "biologic_tnf"],
    [/tinib$/, "jak_inhibitor"],

    // Anticonvulsants
    [/barbital$/, "anticonvulsant"],

    // Antivirals
    [/vir$/, "antiviral"],
    [/ciclovir$/, "antiviral"],
  ];

  for (const [pattern, classId] of patterns) {
    if (pattern.test(nameLower)) {
      return MEDICATION_CLASSES[classId] || null;
    }
  }

  return null;
}

// Storage for custom/unknown medications
const customMedications: Map<string, CustomMedication> = new Map();

/**
 * Add a custom medication (for unknown meds entered by user)
 */
export function addCustomMedication(
  name: string,
  classId: string | null,
  aiInferred: boolean = false
): CustomMedication {
  const custom: CustomMedication = {
    name,
    class_id: classId,
    user_assigned: !aiInferred,
    ai_inferred: aiInferred,
  };
  customMedications.set(name.toLowerCase(), custom);
  return custom;
}

/**
 * Get custom medication if exists
 */
export function getCustomMedication(name: string): CustomMedication | null {
  return customMedications.get(name.toLowerCase()) || null;
}

// ============================================
// LAYER 3: CLASS-BASED REBUTTAL LOGIC
// ============================================

/**
 * Analyze a list of medications and group by class
 */
export function analyzeMedications(medications: string[]): MedicationSummary[] {
  const classCounts: Map<string, { count: number; medications: string[] }> =
    new Map();

  for (const medName of medications) {
    // Try to find the medication
    let med = getMedication(medName);
    let classId: string | null = null;

    if (med) {
      classId = med.class_id;
    } else {
      // Check custom medications
      const custom = getCustomMedication(medName);
      if (custom && custom.class_id) {
        classId = custom.class_id;
      } else {
        // Try to infer class
        const inferred = inferMedicationClass(medName);
        if (inferred) {
          classId = inferred.id;
        } else {
          classId = "misc_other";
        }
      }
    }

    const existing = classCounts.get(classId) || { count: 0, medications: [] };
    existing.count++;
    existing.medications.push(medName);
    classCounts.set(classId, existing);
  }

  // Convert to summaries with rebuttal phrases
  const summaries: MedicationSummary[] = [];

  classCounts.forEach((data, classId) => {
    const medClass = MEDICATION_CLASSES[classId];
    if (!medClass) return;

    // Generate appropriate rebuttal phrase based on count
    let rebuttalPhrase: string;
    if (data.count === 1) {
      rebuttalPhrase =
        medClass.rebuttal_tags[0] ||
        `${medClass.display_name} therapy documented`;
    } else {
      rebuttalPhrase = `Multiple ${medClass.display_name} trials (${data.count}) documented`;
    }

    summaries.push({
      class_id: classId,
      class_name: medClass.display_name,
      count: data.count,
      medications: data.medications,
      rebuttal_phrase: rebuttalPhrase,
      clinical_tags: medClass.clinical_tags,
    });
  });

  // Sort by step therapy level (first-line first) then by count
  summaries.sort((a, b) => {
    const classA = MEDICATION_CLASSES[a.class_id];
    const classB = MEDICATION_CLASSES[b.class_id];
    const levelA = classA?.step_therapy_level || 99;
    const levelB = classB?.step_therapy_level || 99;

    if (levelA !== levelB) return levelA - levelB;
    return b.count - a.count;
  });

  return summaries;
}

/**
 * Generate class-based rebuttal text for a list of medications
 */
export function generateMedicationRebuttal(medications: string[]): string {
  const summaries = analyzeMedications(medications);

  if (summaries.length === 0) {
    return "";
  }

  const parts: string[] = [];

  // Group by category for better organization
  const conservativeTherapies = summaries.filter(
    (s) =>
      MEDICATION_CLASSES[s.class_id]?.clinical_tags.includes("first-line") ||
      MEDICATION_CLASSES[s.class_id]?.clinical_tags.includes(
        "conservative therapy"
      )
  );

  const secondLineTherapies = summaries.filter(
    (s) => (MEDICATION_CLASSES[s.class_id]?.step_therapy_level || 0) === 2
  );

  const specialtyTherapies = summaries.filter(
    (s) =>
      MEDICATION_CLASSES[s.class_id]?.clinical_tags.includes("specialty") ||
      MEDICATION_CLASSES[s.class_id]?.clinical_tags.includes("biologic")
  );

  // Build rebuttal text
  if (conservativeTherapies.length > 0) {
    const phrases = conservativeTherapies.map((s) => s.rebuttal_phrase);
    parts.push(`Conservative therapy attempted: ${phrases.join("; ")}.`);
  }

  if (secondLineTherapies.length > 0) {
    const phrases = secondLineTherapies.map((s) => s.rebuttal_phrase);
    parts.push(`Step therapy requirements met: ${phrases.join("; ")}.`);
  }

  if (specialtyTherapies.length > 0) {
    const phrases = specialtyTherapies.map((s) => s.rebuttal_phrase);
    parts.push(`Advanced/specialty therapy history: ${phrases.join("; ")}.`);
  }

  return parts.join(" ");
}

/**
 * Generate a concise medication summary for display
 * e.g., "NSAIDs (2), SSRI (1), Biologic (1)"
 */
export function generateMedicationSummaryDisplay(
  medications: string[]
): string {
  const summaries = analyzeMedications(medications);

  if (summaries.length === 0) {
    return "No medications documented";
  }

  return summaries.map((s) => `${s.class_name} (${s.count})`).join(", ");
}

/**
 * Get all medications in a specific class
 */
export function getMedicationsByClass(classId: string): Medication[] {
  return MEDICATIONS.filter((med) => med.class_id === classId);
}

/**
 * Get all available medication classes
 */
export function getAllClasses(): MedicationClass[] {
  return Object.values(MEDICATION_CLASSES);
}

/**
 * Get classes grouped by category
 */
export function getClassesByCategory(): Record<string, MedicationClass[]> {
  const grouped: Record<string, MedicationClass[]> = {};

  Object.values(MEDICATION_CLASSES).forEach((cls) => {
    const category = cls.category || "other";
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(cls);
  });

  return grouped;
}

/**
 * Check if conservative therapy has been attempted based on medications
 */
export function hasConservativeTherapy(medications: string[]): boolean {
  const summaries = analyzeMedications(medications);
  return summaries.some(
    (s) =>
      MEDICATION_CLASSES[s.class_id]?.clinical_tags.includes(
        "conservative therapy"
      ) || MEDICATION_CLASSES[s.class_id]?.clinical_tags.includes("first-line")
  );
}

/**
 * Check if step therapy requirements are likely met
 */
export function hasStepTherapyEvidence(
  medications: string[],
  requiredSteps: number = 2
): boolean {
  const summaries = analyzeMedications(medications);
  const stepsCompleted = new Set<number>();

  summaries.forEach((s) => {
    const level = MEDICATION_CLASSES[s.class_id]?.step_therapy_level;
    if (level) stepsCompleted.add(level);
  });

  return stepsCompleted.size >= requiredSteps;
}

/**
 * Get step therapy documentation for rebuttals
 */
export function getStepTherapyDocumentation(medications: string[]): {
  firstLine: MedicationSummary[];
  secondLine: MedicationSummary[];
  thirdLine: MedicationSummary[];
} {
  const summaries = analyzeMedications(medications);

  return {
    firstLine: summaries.filter(
      (s) => (MEDICATION_CLASSES[s.class_id]?.step_therapy_level || 0) === 1
    ),
    secondLine: summaries.filter(
      (s) => (MEDICATION_CLASSES[s.class_id]?.step_therapy_level || 0) === 2
    ),
    thirdLine: summaries.filter(
      (s) => (MEDICATION_CLASSES[s.class_id]?.step_therapy_level || 0) >= 3
    ),
  };
}

/**
 * Format medications for AI prompt context
 * Used when generating call scripts, rebuttals, summaries
 */
export function formatMedicationsForAI(medications: string[]): string {
  const summaries = analyzeMedications(medications);

  if (summaries.length === 0) {
    return "No prior medications documented.";
  }

  const lines: string[] = ["Prior medication history (by class):"];

  summaries.forEach((s) => {
    const medClass = MEDICATION_CLASSES[s.class_id];
    const tags = medClass?.clinical_tags.slice(0, 2).join(", ") || "";
    lines.push(
      `- ${s.class_name} x${s.count}: ${s.medications.join(", ")} [${tags}]`
    );
  });

  return lines.join("\n");
}

/**
 * Get rebuttal suggestions based on medication history
 */
export function getMedicationBasedRebuttals(medications: string[]): string[] {
  const summaries = analyzeMedications(medications);
  const rebuttals: string[] = [];

  // Check for conservative therapy
  const hasConservative = summaries.some((s) =>
    MEDICATION_CLASSES[s.class_id]?.clinical_tags.includes(
      "conservative therapy"
    )
  );
  if (hasConservative) {
    rebuttals.push(
      "Conservative therapy has been attempted without adequate response."
    );
  }

  // Check for multiple agents in same class
  const multipleTrials = summaries.filter((s) => s.count >= 2);
  multipleTrials.forEach((s) => {
    rebuttals.push(
      `Patient has trialed multiple ${s.class_name} agents (${s.count}) without adequate response.`
    );
  });

  // Check for step therapy compliance
  const stepDoc = getStepTherapyDocumentation(medications);
  if (stepDoc.firstLine.length > 0 && stepDoc.secondLine.length > 0) {
    rebuttals.push(
      "Patient has progressed through appropriate step therapy as required."
    );
  }

  // Check for specialty/biologic use
  const hasSpecialty = summaries.some(
    (s) =>
      MEDICATION_CLASSES[s.class_id]?.clinical_tags.includes("specialty") ||
      MEDICATION_CLASSES[s.class_id]?.clinical_tags.includes("biologic")
  );
  if (hasSpecialty) {
    rebuttals.push(
      "Patient's condition has required escalation to specialty/biologic therapy."
    );
  }

  return rebuttals;
}

// Export total count for stats
export const MEDICATION_COUNT = MEDICATIONS.length;
export const CLASS_COUNT = Object.keys(MEDICATION_CLASSES).length;
